import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiX,
  HiSearch,
  HiPrinter,
  HiDocument,
  HiCloudUpload,
  HiKey,
  HiClipboardCopy,
  HiDotsVertical,
  HiCollection,
  HiEye,
  HiExternalLink,
  HiCash,
  HiCheckCircle,
  HiCamera,
  HiRefresh,
  HiLocationMarker,
  HiChevronLeft,
  HiChevronRight,
  HiPaperAirplane,
  HiPhone,
  HiExclamationCircle,
  HiSwitchHorizontal,
} from 'react-icons/hi';
import Select from './Select';
import ClientSearchSelect from './ClientSearchSelect';
import * as HiIcons from 'react-icons/hi';
import { STATUS_LIST, getStatusInfo, getStatusColor, getStatusIcon } from '../utils/statusConfig';
import ConfirmDialog from './ConfirmDialog';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { usePermissions } from '../hooks/usePermissions';
import { SkeletonTable } from './Skeleton';
import { PERMISSAO_MAP } from '../types';
import apiService from '../services/api';
import { normalizeApiData, normalizeImageUrl } from '../utils/apiHelpers';
import { getPickupContact, getPickupLocationText } from '../utils/pickupLocation';
import {
  fetchAllPackageCodigos,
  nextPackageCodeForTipo,
} from '../utils/packageCodeSequence';
import {
  CURRENCY_META,
  LOCAL_CURRENCY_OPTIONS,
  formatMoneyWithFlag,
} from '../utils/currencies';
import { QRCodeSVG } from 'qrcode.react';
import { openWhatsAppApp } from './OpenWhatsAppButton';
import './Boxes.css';

let localIdCounter = 0;

/** Id local único (Date.now() sozinho colide quando vários itens entram no mesmo ms) */
const createLocalId = (prefix = 'local') => {
  localIdCounter += 1;
  return `${prefix}-${Date.now()}-${localIdCounter}`;
};

/** Retorna { date, time } para exibir em duas linhas, ou null se inválido */
const getBoxCreatedAtParts = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return {
    date: d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }),
    time: d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
};

/** Coluna Criado: data e hora em linhas separadas (alias usado na tabela) */
const formatBoxCreatedAt = (iso) => {
  const p = getBoxCreatedAtParts(iso);
  if (!p) return '—';
  return (
    <div className="boxes-table-cell-datetime__inner">
      <span>{p.date}</span>
      <span>{p.time}</span>
    </div>
  );
};

/** Chave estável do invoice na linha (para cor agrupada) */
const getBoxInvoiceKey = (box) => {
  const id =
    box?.invoice_id ?? box?.invoice?.id ?? box?.fatura_id ?? null;
  if (id != null && id !== '') return String(id);
  const codigoInv = box?.codigo_invoice ?? box?.invoice?.codigo_invoice;
  if (codigoInv != null && String(codigoInv).trim() !== '') {
    return String(codigoInv).trim();
  }
  return null;
};

/** Texto da coluna Invoice — prioriza `codigo_invoice` do retorno da API */
const getBoxInvoiceLabel = (box) => {
  const codigoInv = box?.codigo_invoice ?? box?.invoice?.codigo_invoice;
  if (codigoInv != null && String(codigoInv).trim() !== '') {
    return String(codigoInv).trim();
  }
  if (!getBoxInvoiceKey(box)) return null;
  const inv = box?.invoice;
  if (inv && typeof inv === 'object') {
    const s =
      inv.numero_documento ||
      inv.numero ||
      inv.codigo_rastreamento ||
      inv.codigo_invoice ||
      inv.codigo_rastreamento ||
      inv.title ||
      inv.tracking_code;
    if (s != null && String(s).trim() !== '') return String(s).trim();
    if (inv.id != null) return `#${inv.id}`;
  }
  const num = box?.invoice_numero ?? box?.numero_documento_invoice;
  if (num != null && String(num).trim() !== '') return String(num).trim();
  return `#${getBoxInvoiceKey(box)}`;
};

/** Texto único para `search` na API (só busca geral). Invoice e destino filtram no cliente pelas colunas. */
const mergeBoxListSearchQuery = (general) => {
  return general != null ? String(general).trim() : '';
};

/** Mesmo valor exibido na coluna Destino (várias chaves possíveis na API). */
const normalizePackageDestino = (box) => {
  const raw =
    box?.destino ??
    box?.destination ??
    box?.tarifa?.destino ??
    box?.endereco_destino?.cidade ??
    box?.endereco_destino?.nome;
  if (raw == null || String(raw).trim() === '') return '';
  return String(raw).trim();
};

const matchesDestinoFilter = (box, queryTrim) => {
  const q = String(queryTrim || '').trim().toLowerCase();
  if (!q) return true;
  const d = normalizePackageDestino(box).toLowerCase();
  return d.includes(q);
};

/** Mesmo critério da coluna Invoice (`getBoxInvoiceLabel` + id/código para busca parcial). */
const matchesInvoiceFilter = (box, queryTrim) => {
  const q = String(queryTrim || '').trim().toLowerCase();
  if (!q) return true;
  const label = getBoxInvoiceLabel(box);
  const labelLc = label != null ? String(label).toLowerCase() : '';
  if (labelLc.includes(q)) return true;
  const key = getBoxInvoiceKey(box);
  if (key != null && String(key).toLowerCase().includes(q)) return true;
  return false;
};

const INVOICE_ROW_PALETTE_SIZE = 8;

/** Map invoice_id -> classe CSS boxes-row--inv-N (ordem de primeira aparição na página) */
const buildInvoiceRowClassMap = (list) => {
  const map = new Map();
  let n = 0;
  if (!Array.isArray(list)) return map;
  for (const box of list) {
    const key = getBoxInvoiceKey(box);
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, `boxes-row--inv-${n % INVOICE_ROW_PALETTE_SIZE}`);
      n += 1;
    }
  }
  return map;
};

const DESTINO_TEXT_PALETTE_SIZE = 8;

/** Chave estável para agrupar destinos iguais (ignora maiúsculas/minúsculas nas bordas) */
const getDestinoGroupKey = (box) => {
  const d = normalizePackageDestino(box);
  if (!d) return '__empty__';
  return d.toLowerCase();
};

/** Map destino normalizado -> classe de cor do texto (mesma cor para mesmo destino na página) */
const buildDestinoTextClassMap = (list) => {
  const map = new Map();
  let n = 0;
  if (!Array.isArray(list)) return map;
  for (const box of list) {
    const key = getDestinoGroupKey(box);
    if (!map.has(key)) {
      map.set(
        key,
        key === '__empty__'
          ? 'boxes-destino-text--empty'
          : `boxes-destino-text--${n % DESTINO_TEXT_PALETTE_SIZE}`
      );
      if (key !== '__empty__') n += 1;
    }
  }
  return map;
};

/** Valor padrão (cm) para dimensões em nova caixa/mala */
const DEFAULT_DIMENSAO_CM = '10';

/** ID da tarifa padrão (fallback) quando valor da remessa não foi calculado/preenchido. */
const DEFAULT_GERENTE_TARIFA_ID = 1;

/** Valor da remessa (R$/kg) × peso quando Gerente cria e não há valor preenchido/calculado. */
const GERENTE_VALOR_REMESA_POR_KG = 65;

const getClienteCodigoFromBox = (box) => {
  if (!box) return '';
  let codigos = box.codigos_acesso;
  if (typeof codigos === 'string' && codigos.trim() !== '') {
    try {
      codigos = JSON.parse(codigos);
    } catch (e) {
      codigos = null;
    }
  }
  const c = codigos?.cliente;
  return c != null && String(c).trim() !== '' ? String(c).trim() : '';
};

const Boxes = () => {
  const navigate = useNavigate();
  const { userRole, loading: permissionsLoading } = usePermissions();
  const isBoxesAdmin = useMemo(() => {
    if (permissionsLoading) return false;
    if (!userRole) return false;
    return PERMISSAO_MAP[Number(userRole.permissaos_id)] === 'Admin';
  }, [userRole, permissionsLoading]);
  const canCreateEditBoxes = useMemo(() => {
    if (permissionsLoading) return false;
    if (!userRole) return false;
    const role = PERMISSAO_MAP[Number(userRole.permissaos_id)];
    return role === 'Admin' || role === 'Gerente';
  }, [userRole, permissionsLoading]);
  /** Admin e Gerente veem e editam a etapa de Tarifas no wizard. */
  const canManageBoxTarifas = canCreateEditBoxes;

  /** Etapas do wizard na barra de progresso (Admin/Gerente veem Tarifas). */
  const wizardStepsDisplay = useMemo(
    () =>
      canManageBoxTarifas
        ? [
            { step: 1, label: 'Básico' },
            { step: 2, label: 'Dimensões' },
            { step: 3, label: 'Tarifas' },
            { step: 4, label: 'Conteúdo' },
            { step: 5, label: 'Origem/Destino' },
            { step: 6, label: 'Destinatário' },
            { step: 7, label: 'Nota Fiscal' },
            { step: 8, label: 'Itens' },
          ]
        : [
            { step: 1, label: 'Básico' },
            { step: 2, label: 'Dimensões' },
            { step: 4, label: 'Conteúdo' },
            { step: 5, label: 'Origem/Destino' },
            { step: 6, label: 'Destinatário' },
            { step: 7, label: 'Nota Fiscal' },
            { step: 8, label: 'Itens' },
          ],
    [canManageBoxTarifas]
  );

  const [boxes, setBoxes] = useState([]);
  const [clients, setClients] = useState([]);
  const [tarifas, setTarifas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [totalSteps] = useState(8);
  const [boxListFilters, setBoxListFilters] = useState({
    search: '',
    invoice: '',
    destino: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalBoxes, setTotalBoxes] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [formData, setFormData] = useState({
    codigo: '',
    tipo: 'caixa',
    cliente_id: '',
    cliente_nome: '',
    peso: '',
    dimensoes: {
      comprimento: DEFAULT_DIMENSAO_CM,
      largura: DEFAULT_DIMENSAO_CM,
      altura: DEFAULT_DIMENSAO_CM
    },
    valor_remessa: '',
    tarifa_id: '',
    tarifa_intermediacao: '',
    tarifa_coleta: '',
    conteudo: '',
    observacao: '',
    status: 'pedido_criado',
    origem: '',
    destino: '',
    nome_destinatario: '',
    telefone_destinatario: '',
    pais_transito: '',
    codigo_rastreio: '',
    codigo_atualizacao: '',
    codigos_acesso: {
      cliente: ''
    },
    itens: [],
    nota_fiscal: null
  });
  const [editingId, setEditingId] = useState(null);
  const [currentItem, setCurrentItem] = useState({
    nome: '',
    ncm: '',
    cfop: '',
    descricao: '',
    peso: '',
    preco: '',
    quantidade: 1,
    unidade: '',
    fotos: [] // Inicializado como array vazio
  });
  const [ncmSuggestions, setNcmSuggestions] = useState([]);
  const [showNcmDropdown, setShowNcmDropdown] = useState(false);
  const [invalidFields, setInvalidFields] = useState({});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [boxToDelete, setBoxToDelete] = useState(null);
  const [showCodesModal, setShowCodesModal] = useState(false);
  const [selectedBoxCodes, setSelectedBoxCodes] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  /** Todos os codigo_pacote já carregados — sequência por prefixo (CX/ML/PL/CT) */
  const packageCodesRef = useRef([]);
  const [loadingSequenceCodes, setLoadingSequenceCodes] = useState(false);
  const [coletasForModal, setColetasForModal] = useState([]);
  const [selectedColetaId, setSelectedColetaId] = useState('');
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [selectedBoxItems, setSelectedBoxItems] = useState(null);
  const [showNotaFiscalModal, setShowNotaFiscalModal] = useState(false);
  const [selectedNotaFiscal, setSelectedNotaFiscal] = useState(null);
  const [extractingNf, setExtractingNf] = useState(false);
  // Ids dos documentos de NF que o usuário removeu na edição; enviados no save
  // para o backend apagar documento, arquivo e itens que vieram daquela nota.
  const [notasFiscaisRemovidas, setNotasFiscaisRemovidas] = useState([]);
  const [showDeleteItemDialog, setShowDeleteItemDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showViewPaymentModal, setShowViewPaymentModal] = useState(false);
  const [showCancelPaymentModal, setShowCancelPaymentModal] = useState(false);
  const [cancelJustification, setCancelJustification] = useState('');
  const [selectedBoxForPayment, setSelectedBoxForPayment] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedBoxDelivery, setSelectedBoxDelivery] = useState(null);
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [selectedBoxRecipient, setSelectedBoxRecipient] = useState(null);
  const [recipientCobrancaId, setRecipientCobrancaId] = useState('');
  const [showPendenciaModal, setShowPendenciaModal] = useState(false);
  const [selectedBoxPendencia, setSelectedBoxPendencia] = useState(null);
  const [pendenciaForm, setPendenciaForm] = useState({
    valor_brl: '',
    moeda_local: 'XOF',
    observacao: '',
  });
  const [pendenciaPreview, setPendenciaPreview] = useState(null);
  const [savingPendencia, setSavingPendencia] = useState(false);
  const [convertingPendencia, setConvertingPendencia] = useState(false);
  const [boxPayment, setBoxPayment] = useState({
    valor: '',
    data: '',
    metodo: 'dinheiro',
    comprovantes: [],
  });
  const emptyBoxPayment = () => ({
    valor: '',
    data: new Date().toISOString().split('T')[0],
    metodo: 'dinheiro',
    comprovantes: [],
  });
  const roundMoney = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const parseMoneyLike = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const numeroStr = String(value).replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
    const n = parseFloat(numeroStr);
    return Number.isFinite(n) ? n : 0;
  };
  const formatNumberAsCurrencyInput = (numero) => {
    if (!(numero > 0)) return '';
    return formatCurrency(String(Math.round(numero * 100)));
  };
  const getBoxPaymentBalances = (box) => {
    const total = parseMoneyLike(box?.valor_declarado || box?.valor_remessa);
    const pago = parseMoneyLike(box?.valor_pago);
    const restanteApi = box?.valor_restante;
    const restante =
      restanteApi !== undefined && restanteApi !== null && restanteApi !== ''
        ? parseMoneyLike(restanteApi)
        : Math.max(0, roundMoney(total - pago));
    return { total, pago, restante };
  };
  const openRegisterPaymentModal = (box) => {
    const { restante, total } = getBoxPaymentBalances(box);
    const prefill = restante > 0 ? restante : total;
    setSelectedBoxForPayment(box);
    setBoxPayment({
      ...emptyBoxPayment(),
      valor: formatNumberAsCurrencyInput(prefill),
    });
    setShowPaymentModal(true);
  };
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraForItem, setCameraForItem] = useState(false); // true se for para item, false se for para comprovante
  const [cameraForNotaFiscal, setCameraForNotaFiscal] = useState(false); // true se for para nota fiscal
  const [cameraForComprovante, setCameraForComprovante] = useState(false); // true se for para comprovante
  const [cameraFacingMode, setCameraFacingMode] = useState('environment'); // 'environment' = traseira, 'user' = frontal
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const modalFormRef = useRef(null);
  const wizardProgressRef = useRef(null);
  const { notifications, removeNotification, showSuccess, showWarning } = useNotification();

  // Ao mudar de etapa no wizard: rolar o conteúdo do modal para o topo e destacar a aba atual (rolagem horizontal em mobile/tablet)
  useEffect(() => {
    if (showModal && modalFormRef.current) {
      modalFormRef.current.scrollTop = 0;
    }
    if (showModal && wizardProgressRef.current) {
      const activeEl = wizardProgressRef.current.querySelector('.wizard-step.active');
      if (activeEl) {
        const t = setTimeout(() => {
          activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 50);
        return () => clearTimeout(t);
      }
    }
  }, [currentStep, showModal]);

  useEffect(() => {
    loadBoxes();
    loadClients();
    loadTarifas();
  }, []);

  useRealtimeRefresh(
    ['packages', 'invoices', 'payments', 'documents', 'coletas', 'clients'],
    () => {
      loadBoxes();
      loadClients();
    },
    { paused: showModal }
  );

  // Perfis sem acesso a Tarifas não usam etapa 3; evita ficar preso nela
  useEffect(() => {
    if (showModal && !canManageBoxTarifas && currentStep === 3) {
      setCurrentStep(4);
    }
  }, [showModal, canManageBoxTarifas, currentStep]);

  // Busca com debounce: texto geral vai para a API; Invoice e Destino filtram no cliente (mesmas colunas da tabela).
  const searchInitialMount = useRef(true);
  useEffect(() => {
    if (searchInitialMount.current) {
      searchInitialMount.current = false;
      return;
    }
    const t = setTimeout(() => {
      setCurrentPage(1);
      loadBoxes({
        page: 1,
        search: boxListFilters.search,
        codigo_invoice: boxListFilters.invoice,
        destino: boxListFilters.destino,
      });
    }, 400);
    return () => clearTimeout(t);
  }, [boxListFilters.search, boxListFilters.invoice, boxListFilters.destino]);

  // Fechar menu quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.actions-menu-wrapper')) {
        setOpenMenuId(null);
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  // Fechar dropdown de NCM ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNcmDropdown && !event.target.closest('.ncm-dropdown') && !event.target.closest('input[placeholder="1234.56.78"]')) {
        setShowNcmDropdown(false);
      }
    };

    if (showNcmDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNcmDropdown]);

  const loadClients = async () => {
    try {
      const perPage = 500;
      let page = 1;
      const all = [];
      for (;;) {
        const response = await apiService.getClientsList({
          page,
          per_page: perPage,
        });
        const list = normalizeApiData(response);
        if (!Array.isArray(list) || list.length === 0) break;
        all.push(...list);
        const lastPage = response?.last_page ?? response?.lastPage;
        if (lastPage !== undefined && lastPage !== null) {
          if (page >= Number(lastPage)) break;
          page += 1;
          continue;
        }
        if (list.length < perPage) break;
        page += 1;
      }
      setClients(
        all.map((c) => ({ id: c.id, nome: c.name || c.nome || '' }))
      );
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setClients([]);
    }
  };

  const loadTarifas = async () => {
    try {
      const data = await apiService.getTarifas({ per_page: 200 });
      const tarifasList = normalizeApiData(data);
      setTarifas(Array.isArray(tarifasList) ? tarifasList : []);
    } catch (error) {
      console.error('Erro ao carregar tarifas:', error);
      setTarifas([]);
    }
  };

  const loadBoxes = async (overrides = {}) => {
    const page = overrides.page !== undefined ? overrides.page : currentPage;
    const perPage = overrides.per_page !== undefined ? overrides.per_page : itemsPerPage;
    const search =
      overrides.search !== undefined ? overrides.search : boxListFilters.search;
    const codigoInvoice =
      overrides.codigo_invoice !== undefined
        ? overrides.codigo_invoice
        : boxListFilters.invoice;
    const destinoFilter =
      overrides.destino !== undefined ? overrides.destino : boxListFilters.destino;
    const invTrim =
      codigoInvoice && String(codigoInvoice).trim()
        ? String(codigoInvoice).trim()
        : '';
    const destTrim =
      destinoFilter && String(destinoFilter).trim()
        ? String(destinoFilter).trim()
        : '';
    const mergedSearchForApi = mergeBoxListSearchQuery(search);
    const needsClientInvoiceFilter = !!invTrim;
    const needsClientDestinoFilter = !!destTrim;
    const needsClientColumnFilter = needsClientInvoiceFilter || needsClientDestinoFilter;

    const mapBoxRow = (box) => ({
      ...box,
      codigo: box.codigo_pacote || box.codigo || '',
      destino: normalizePackageDestino(box),
      cliente_nome:
        box.cliente?.nome ||
        box.cliente?.name ||
        box.cliente_nome ||
        box.cliente_name ||
        (box.cliente && typeof box.cliente === 'string' ? box.cliente : '') ||
        '',
      cliente_id: box.cliente_id || box.cliente?.id || '',
      dimensoes: {
        comprimento: box.comprimento || box.dimensoes?.comprimento || '',
        largura: box.largura || box.dimensoes?.largura || '',
        altura: box.altura || box.dimensoes?.altura || ''
      },
      peso: (() => {
        const pesoValue = box.peso_bruto || box.peso;
        if (!pesoValue) return '';
        if (typeof pesoValue === 'number') {
          return formatWeight(String(Math.round(pesoValue * 100)));
        }
        const pesoStr = String(pesoValue).replace(',', '.');
        const pesoNum = parseFloat(pesoStr);
        if (!isNaN(pesoNum)) {
          return formatWeight(String(Math.round(pesoNum * 100)));
        }
        return formatWeight(pesoStr);
      })(),
      valor_remessa: box.valor_declarado || box.valor_remessa || '',
      observacao: box.observacao || '',
      status_pagamento: box.status_pagamento || null,
      codigos_acesso: (() => {
        let codigos = box.codigos_acesso;
        if (typeof codigos === 'string' && codigos.trim() !== '') {
          try {
            codigos = JSON.parse(codigos);
          } catch (e) {
            console.error('Erro ao fazer parse de codigos_acesso:', e);
            codigos = null;
          }
        }
        return codigos && typeof codigos === 'object'
          ? { cliente: codigos.cliente || '' }
          : { cliente: '' };
      })(),
      itens: box.itens || [],
      nota_fiscal: box.nota_fiscal_url || box.nota_fiscal || null,
      status: box.status || 'pedido_criado',
      invoice_id: box.invoice_id ?? box.invoice?.id ?? box.fatura_id ?? null,
      codigo_invoice:
        box.codigo_invoice ??
        box.invoice?.codigo_invoice ??
        null
    });

    try {
      const apiParamsBase = {
        ...(mergedSearchForApi ? { search: mergedSearchForApi } : {})
      };

      if (needsClientColumnFilter) {
        const fetchPerPage = 100;
        const allMapped = [];
        let p = 1;
        for (;;) {
          const response = await apiService.getBoxes({
            ...apiParamsBase,
            page: p,
            per_page: fetchPerPage
          });
          const boxesList = normalizeApiData(response);
          if (!Array.isArray(boxesList) || boxesList.length === 0) break;
          allMapped.push(...boxesList.map(mapBoxRow));
          const lastPg = Number(response?.last_page ?? response?.lastPage ?? 1);
          if (p >= lastPg) break;
          if (boxesList.length < fetchPerPage) break;
          p += 1;
        }
        let filtered = allMapped;
        if (needsClientInvoiceFilter) {
          filtered = filtered.filter((row) => matchesInvoiceFilter(row, invTrim));
        }
        if (needsClientDestinoFilter) {
          filtered = filtered.filter((row) => matchesDestinoFilter(row, destTrim));
        }
        const totalF = filtered.length;
        const lastPageF = Math.max(1, Math.ceil(totalF / perPage) || 1);
        const pageUse = Math.min(Math.max(1, page), lastPageF);
        const start = (pageUse - 1) * perPage;
        const pageSlice = filtered.slice(start, start + perPage);
        setBoxes(pageSlice);
        setTotalBoxes(totalF);
        setLastPage(lastPageF);
        setCurrentPage(pageUse);
      } else {
        const response = await apiService.getBoxes({
          ...apiParamsBase,
          page,
          per_page: perPage
        });
        const boxesList = normalizeApiData(response);
        const boxesWithDefaults = boxesList.map(mapBoxRow);
        setBoxes(boxesWithDefaults);
        setTotalBoxes(response.total ?? 0);
        setLastPage(response.last_page ?? 1);
        if (overrides.page !== undefined) setCurrentPage(overrides.page);
      }
      if (overrides.per_page !== undefined) setItemsPerPage(overrides.per_page);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar boxes:', error);
      setBoxes([]);
      setTotalBoxes(0);
      setLastPage(1);
      setLoading(false);
    }
  };

  const loadAllCodesForSequence = () =>
    fetchAllPackageCodigos((params) => apiService.getBoxes(params));

  // Função para gerar código de acesso único
  const generateAccessCode = (prefix = '') => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = prefix;
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  // Função para gerar código de acesso do cliente
  const generateClientAccessCode = () => {
    return {
      cliente: generateAccessCode('CLI')
    };
  };

  // Função para formatar valor como moeda brasileira
  const formatCurrency = (value) => {
    if (!value) return '';
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '');
    if (numbers === '') return '';
    // Converte para número e formata
    const number = parseFloat(numbers) / 100;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(number);
  };

  // Função para formatar peso com ponto como separador decimal (similar à máscara de moeda)
  const formatWeight = (value) => {
    if (!value) return '';
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '');
    if (numbers === '') return '';
    // Converte para número e divide por 100 (similar à moeda)
    const number = parseFloat(numbers) / 100;
    // Formata com ponto como separador decimal e 2 casas decimais
    return number.toFixed(2).replace(',', '.');
  };

  // Função para exibir valor formatado
  const displayCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    // Se já está formatado, retorna
    if (typeof value === 'string' && value.includes('R$')) {
      return value;
    }
    // Converte número para formato brasileiro
    const number = typeof value === 'string'
      ? parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.')) || 0
      : value || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(number);
  };

  // Função para converter peso de string para número
  const parseWeight = (weightStr) => {
    if (!weightStr) return 0;
    // Remove espaços e converte vírgula para ponto
    const cleaned = weightStr.toString().trim().replace(',', '.');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Função para validar e destacar campos inválidos
  const validateAndHighlightField = (fieldName, message, stepNumber = null) => {
    // Limpar validações anteriores
    setInvalidFields({});

    // Destacar campo inválido
    setTimeout(() => {
      setInvalidFields({ [fieldName]: true });

      // Navegar até o campo
      const fieldId = `field-${fieldName}`;
      const fieldElement = document.getElementById(fieldId) ||
                          document.querySelector(`[name="${fieldName}"]`) ||
                          document.querySelector(`input[placeholder*="${fieldName}"]`);

      if (fieldElement) {
        // Scroll até o campo
        fieldElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        // Focar no campo
        setTimeout(() => {
          fieldElement.focus();
          if (fieldElement.select) {
            fieldElement.select();
          }
        }, 300);
      } else if (stepNumber) {
        // Se não encontrou o campo, navegar até o step do wizard
        setCurrentStep(stepNumber);
        setTimeout(() => {
          const stepElement = document.querySelector(`.wizard-step-content.active`);
          if (stepElement) {
            stepElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
          // Tentar encontrar o campo novamente após mudar de step
          setTimeout(() => {
            const fieldElementAfterStep = document.getElementById(fieldId) ||
                                        document.querySelector(`[name="${fieldName}"]`);
            if (fieldElementAfterStep) {
              fieldElementAfterStep.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
              setTimeout(() => {
                fieldElementAfterStep.focus();
                if (fieldElementAfterStep.select) {
                  fieldElementAfterStep.select();
                }
              }, 200);
            }
          }, 300);
        }, 100);
      }
    }, 100);

    // Mostrar mensagem de erro
    showWarning(message);
  };

  // Função para buscar NCMs baseado no nome do item
  const searchNcmByProductName = (productName) => {
    if (!productName || productName.trim().length < 2) {
      return [];
    }

    const nameLower = productName.toLowerCase().trim();

    // Mapeamento de palavras-chave para NCMs comuns
    const ncmMap = {
      // Roupas e Calçados
      'camisa': ['6109.10.00', '6109.90.00', '6205.20.00'],
      'camiseta': ['6109.10.00', '6109.90.00'],
      'calça': ['6203.42.00', '6203.43.00', '6203.49.00'],
      'bermuda': ['6203.42.00', '6203.49.00'],
      'short': ['6203.42.00', '6203.49.00'],
      'vestido': ['6204.42.00', '6204.43.00', '6204.44.00'],
      'sapato': ['6403.99.00', '6404.19.00'],
      'tênis': ['6404.11.00', '6404.19.00'],
      'chinelo': ['6402.20.00', '6402.99.00'],
      'sandália': ['6402.20.00', '6402.99.00'],
      'bolsa': ['4202.12.00', '4202.22.00', '4202.32.00'],
      'mochila': ['4202.12.00', '4202.92.00'],
      'cinto': ['4203.30.00'],

      // Eletrônicos
      'celular': ['8517.12.00', '8517.70.90'],
      'smartphone': ['8517.12.00', '8517.70.90'],
      'telefone': ['8517.12.00', '8517.70.90'],
      'tablet': ['8471.30.00', '8517.12.00'],
      'notebook': ['8471.30.00'],
      'computador': ['8471.30.00', '8471.50.00'],
      'fone': ['8518.30.00', '8518.50.00'],
      'fone de ouvido': ['8518.30.00', '8518.50.00'],
      'carregador': ['8504.40.00', '8504.50.00'],
      'cabo': ['8544.42.00', '8544.49.00'],

      // Cosméticos e Perfumaria
      'perfume': ['3303.00.00'],
      'colônia': ['3303.00.00'],
      'shampoo': ['3305.10.00', '3305.90.00'],
      'condicionador': ['3305.10.00', '3305.90.00'],
      'sabonete': ['3401.11.00', '3401.19.00'],
      'creme': ['3304.99.00', '3304.91.00'],
      'maquiagem': ['3304.20.00', '3304.30.00'],
      'batom': ['3304.20.00'],
      'esmalte': ['3304.30.00'],

      // Alimentos
      'chocolate': ['1806.31.00', '1806.32.00', '1806.90.00'],
      'café': ['0901.11.00', '0901.12.00', '0901.21.00'],
      'açúcar': ['1701.14.00', '1701.99.00'],
      'arroz': ['1006.30.00', '1006.40.00'],
      'feijão': ['0713.32.00', '0713.33.00'],
      'óleo': ['1507.90.00', '1509.90.00'],
      'azeite': ['1509.90.00'],

      // Medicamentos
      'medicamento': ['3004.90.00', '3004.50.00'],
      'remédio': ['3004.90.00', '3004.50.00'],
      'vitamina': ['2936.27.00', '2936.29.00'],
      'suplemento': ['2106.90.00', '2936.27.00'],

      // Brinquedos
      'brinquedo': ['9503.00.00'],
      'boneca': ['9503.00.00'],
      'carrinho': ['9503.00.00'],
      'jogo': ['9504.30.00', '9504.90.00'],

      // Livros e Papelaria
      'livro': ['4901.10.00', '4901.99.00'],
      'caderno': ['4820.20.00', '4820.30.00'],
      'caneta': ['9608.10.00', '9608.20.00'],
      'lápis': ['9609.10.00', '9609.20.00'],

      // Eletrodomésticos
      'geladeira': ['8418.10.00', '8418.21.00'],
      'fogão': ['7321.11.00', '7321.12.00'],
      'microondas': ['8516.50.00'],
      'liquidificador': ['8509.40.00'],
      'ventilador': ['8414.51.00', '8414.59.00'],

      // Ferramentas
      'ferramenta': ['8205.40.00', '8205.51.00'],
      'chave': ['8204.11.00', '8204.12.00'],
      'martelo': ['8205.20.00'],
      'chave de fenda': ['8205.40.00'],

      // Material de Construção
      'cimento': ['2523.29.00'],
      'tijolo': ['6904.10.00', '6904.90.00'],
      'telha': ['6905.10.00', '6905.90.00'],

      // Outros
      'óculos': ['9004.10.00', '9004.90.00'],
      'relógio': ['9102.11.00', '9102.12.00', '9102.19.00'],
      'joia': ['7113.19.00', '7113.20.00'],
      'bijuteria': ['7117.19.00', '7117.90.00'],
      'cigarro': ['2402.20.00'],
      'bebida': ['2203.00.00', '2208.20.00'],
      'cerveja': ['2203.00.00'],
      'vinho': ['2204.21.00', '2204.29.00']
    };

    // Buscar NCMs que correspondem às palavras-chave
    const suggestions = [];
    const words = nameLower.split(/\s+/);

    for (const [keyword, ncms] of Object.entries(ncmMap)) {
      if (nameLower.includes(keyword) || words.some(word => word.includes(keyword) || keyword.includes(word))) {
        ncms.forEach(ncm => {
          if (!suggestions.includes(ncm)) {
            suggestions.push(ncm);
          }
        });
      }
    }

    // Se não encontrou sugestões específicas, retornar NCMs genéricos comuns
    if (suggestions.length === 0) {
      return [
        '8471.30.00', // Máquinas automáticas para processamento de dados
        '8517.12.00', // Telefones celulares
        '6404.19.00', // Calçados com solas de borracha ou plástico
        '4202.12.00', // Bolsas de mão
        '3303.00.00', // Perfumes e águas de colônia
        '6109.10.00', // Camisetas de malha
        '6203.42.00', // Calças de algodão
        '3004.90.00'  // Medicamentos
      ];
    }

    return suggestions.slice(0, 10); // Limitar a 10 sugestões
  };

  // Função para atualizar sugestões de NCM quando o nome do item muda
  const handleItemNameChange = (e) => {
    const newName = e.target.value;
    setCurrentItem({ ...currentItem, nome: newName });

    if (newName && newName.trim().length >= 2) {
      const suggestions = searchNcmByProductName(newName);
      setNcmSuggestions(suggestions);
      setShowNcmDropdown(suggestions.length > 0);
    } else {
      setNcmSuggestions([]);
      setShowNcmDropdown(false);
    }
  };

  // Função para converter valor de moeda formatado para número
  const parseCurrency = (currencyStr) => {
    if (!currencyStr) return 0;
    // Remove R$, espaços e converte formato brasileiro para número
    const cleaned = currencyStr.toString()
      .replace('R$', '')
      .replace(/\./g, '') // Remove pontos (separadores de milhar)
      .replace(',', '.') // Converte vírgula decimal para ponto
      .trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Função para formatar número como moeda brasileira (sem dividir por 100)
  const formatNumberAsCurrency = (value) => {
    const number = typeof value === 'string'
      ? parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.')) || 0
      : value || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(number);
  };

  // Função para calcular tarifa baseada no peso
  const calculateTarifa = (tarifaId, peso, tarifaIntermediacaoAtual = '', tarifaColetaAtual = '') => {
    if (!tarifaId) {
      return { tarifa_intermediacao: '', tarifa_coleta: '', valor_remessa: '' };
    }

    const tarifa = tarifas.find(t => t.id === tarifaId || t.id === parseInt(tarifaId));
    if (!tarifa) {
      return { tarifa_intermediacao: '', tarifa_coleta: '', valor_remessa: '' };
    }

    // Se não há peso informado, retornar valores vazios mas manter tarifa selecionada
    if (!peso) {
      return { tarifa_intermediacao: '', tarifa_coleta: '', valor_remessa: '' };
    }

    const pesoNum = parseWeight(peso);
    const valorTarifa = parseFloat(tarifa.valor) || 0;
    const acrescimentoEmbalagem = parseFloat(tarifa.acrescimento_embalagem) || 0;

    // Converter valores atuais de tarifa_intermediacao e tarifa_coleta para números
    const tarifaIntermediacaoNum = parseCurrency(tarifaIntermediacaoAtual);
    const tarifaColetaNum = parseCurrency(tarifaColetaAtual);

    // Sempre calcular valor base: valor_tarifa * peso (independente de ser maior ou menor que kg_max)
    const valorBase = valorTarifa * pesoNum;

    // Calcular valor total: valor_base + acrescimento_embalagem + tarifa_intermediacao + tarifa_coleta
    const valorTotal = valorBase + acrescimentoEmbalagem + tarifaIntermediacaoNum + tarifaColetaNum;
    const valorTotalFormatado = formatNumberAsCurrency(valorTotal);

    return {
      tarifa_intermediacao: tarifaIntermediacaoAtual || '',
      tarifa_coleta: tarifaColetaAtual || '',
      valor_remessa: valorTotalFormatado || ''
    };
  };

  // Função para lidar com mudança de tarifa
  const handleTarifaChange = (tarifaId) => {
    if (!canManageBoxTarifas) return;
    // Validar se peso foi informado
    if (tarifaId && !formData.peso) {
      showWarning('Por favor, informe o peso antes de selecionar uma tarifa');
      return;
    }

    // Buscar tarifa selecionada
    const tarifaSelecionada = tarifas.find(t => t.id === tarifaId || t.id === parseInt(tarifaId));

    // Calcular tarifas automaticamente, passando valores atuais
    const tarifasCalculadas = calculateTarifa(
      tarifaId,
      formData.peso,
      formData.tarifa_intermediacao,
      formData.tarifa_coleta
    );

    // Preparar dados do formulário
    const newFormData = {
      ...formData,
      tarifa_id: tarifaId,
      tarifa_intermediacao: tarifasCalculadas.tarifa_intermediacao,
      tarifa_coleta: tarifasCalculadas.tarifa_coleta,
      // Sempre atualizar valor_remessa quando houver cálculo (mesmo que seja string vazia)
      valor_remessa: tarifasCalculadas.valor_remessa !== undefined ? tarifasCalculadas.valor_remessa : formData.valor_remessa
    };

    // Se uma tarifa foi selecionada, preencher origem e destino
    if (tarifaSelecionada) {
      newFormData.origem = tarifaSelecionada.origem || '';
      newFormData.destino = tarifaSelecionada.destino || '';
    } else {
      // Se nenhuma tarifa foi selecionada, limpar origem e destino
      newFormData.origem = '';
      newFormData.destino = '';
    }

    setFormData(newFormData);
  };

  // Função para lidar com mudança de peso
  const handlePesoChange = (peso) => {
    const formatted = formatWeight(peso);
    const newFormData = { ...formData, peso: formatted };

    // Se há uma tarifa selecionada, recalcular
    // Usar o peso formatado (já está com ponto como separador decimal)
    if (formData.tarifa_id) {
      const tarifasCalculadas = calculateTarifa(
        formData.tarifa_id,
        formatted,
        formData.tarifa_intermediacao,
        formData.tarifa_coleta
      );
      newFormData.tarifa_intermediacao = tarifasCalculadas.tarifa_intermediacao;
      newFormData.tarifa_coleta = tarifasCalculadas.tarifa_coleta;
      // Sempre atualizar valor_remessa quando houver cálculo (mesmo que seja string vazia)
      newFormData.valor_remessa = tarifasCalculadas.valor_remessa !== undefined ? tarifasCalculadas.valor_remessa : newFormData.valor_remessa;
      if (!isBoxesAdmin && formatted) {
        const pesoW = parseWeight(formatted);
        if (
          pesoW > 0 &&
          (!String(newFormData.valor_remessa).trim() ||
            parseCurrency(newFormData.valor_remessa) <= 0)
        ) {
          newFormData.valor_remessa = formatNumberAsCurrency(
            GERENTE_VALOR_REMESA_POR_KG * pesoW
          );
        }
      }
    }

    setFormData(newFormData);
  };

  // Função para recalcular valor_remessa quando tarifa_intermediacao ou tarifa_coleta mudarem
  const recalculateValorRemessa = (tarifaIntermediacao, tarifaColeta) => {
    // Só recalcular se há tarifa selecionada e peso informado
    if (!formData.tarifa_id || !formData.peso) {
      return formData.valor_remessa;
    }

    const tarifa = tarifas.find(t => t.id === formData.tarifa_id || t.id === parseInt(formData.tarifa_id));
    if (!tarifa) {
      return formData.valor_remessa;
    }

    const pesoNum = parseWeight(formData.peso);
    const valorTarifa = parseFloat(tarifa.valor) || 0;
    const acrescimentoEmbalagem = parseFloat(tarifa.acrescimento_embalagem) || 0;

    // Sempre calcular valor base: valor_tarifa * peso (independente de ser maior ou menor que kg_max)
    const valorBase = valorTarifa * pesoNum;

    const tarifaIntermediacaoNum = parseCurrency(tarifaIntermediacao);
    const tarifaColetaNum = parseCurrency(tarifaColeta);

    // Calcular valor total: valor_base + acrescimento_embalagem + tarifa_intermediacao + tarifa_coleta
    const valorTotal = valorBase + acrescimentoEmbalagem + tarifaIntermediacaoNum + tarifaColetaNum;
    return formatNumberAsCurrency(valorTotal);
  };

  // Função para lidar com mudança de tarifa_intermediacao
  const handleTarifaIntermediacaoChange = (value) => {
    if (!canManageBoxTarifas) return;
    const formatted = formatCurrency(value);
    const novoValorRemessa = recalculateValorRemessa(formatted, formData.tarifa_coleta);
    setFormData({
      ...formData,
      tarifa_intermediacao: formatted,
      valor_remessa: novoValorRemessa
    });
  };

  // Função para lidar com mudança de tarifa_coleta
  const handleTarifaColetaChange = (value) => {
    if (!canManageBoxTarifas) return;
    const formatted = formatCurrency(value);
    const novoValorRemessa = recalculateValorRemessa(formData.tarifa_intermediacao, formatted);
    setFormData({
      ...formData,
      tarifa_coleta: formatted,
      valor_remessa: novoValorRemessa
    });
  };

  const loadColetasForModal = async () => {
    try {
      const response = await apiService.getColetas({ page: 1, per_page: 200 });
      const list = normalizeApiData(response);
      const mapped = (Array.isArray(list) ? list : []).map((row) => ({
        ...row,
        codigo: row.codigo_pacote || row.codigo || '',
        cliente_nome:
          row.cliente_nome ||
          row.cliente?.nome ||
          row.cliente?.name ||
          row.cliente_name ||
          '',
      }));
      setColetasForModal(mapped);
    } catch (e) {
      console.error('Erro ao carregar coletas:', e);
      setColetasForModal([]);
    }
  };

  const applyFormPrefillFromColeta = (coleta) => {
    setFormData((prev) => {
      const pesoRaw = coleta.peso_bruto ?? coleta.peso;
      let pesoFormatted = '';
      if (pesoRaw !== null && pesoRaw !== undefined && pesoRaw !== '') {
        const n =
          typeof pesoRaw === 'number'
            ? pesoRaw
            : parseFloat(String(pesoRaw).replace(',', '.'));
        if (!Number.isNaN(n) && n > 0) {
          pesoFormatted = formatWeight(String(Math.round(n * 100)));
        }
      }

      const clienteNome =
        coleta.cliente_nome ||
        coleta.cliente?.nome ||
        coleta.cliente?.name ||
        clients.find((c) => String(c.id) === String(coleta.cliente_id))?.nome ||
        '';

      let codigos = coleta.codigos_acesso;
      if (typeof codigos === 'string' && codigos.trim() !== '') {
        try {
          codigos = JSON.parse(codigos);
        } catch {
          codigos = null;
        }
      }
      const codigosAcesso =
        codigos && typeof codigos === 'object' && codigos.cliente
          ? { cliente: codigos.cliente }
          : prev.codigos_acesso;

      const tarifaId = coleta.tarifa_id ? String(coleta.tarifa_id) : '';

      const next = {
        ...prev,
        cliente_id:
          coleta.cliente_id != null && coleta.cliente_id !== ''
            ? String(coleta.cliente_id)
            : prev.cliente_id,
        cliente_nome: clienteNome || prev.cliente_nome,
        origem: coleta.origem != null ? String(coleta.origem) : '',
        destino: coleta.destino != null ? String(coleta.destino) : '',
        peso: pesoFormatted,
        tarifa_id: tarifaId,
        nome_destinatario: coleta.nome_destinatario || '',
        telefone_destinatario: coleta.telefone_destinatario || '',
        codigos_acesso: codigosAcesso,
      };

      if (tarifaId && pesoFormatted) {
        const t = calculateTarifa(
          tarifaId,
          pesoFormatted,
          next.tarifa_intermediacao || '',
          next.tarifa_coleta || ''
        );
        next.valor_remessa = t.valor_remessa;
        next.tarifa_intermediacao = t.tarifa_intermediacao;
        next.tarifa_coleta = t.tarifa_coleta;
      } else if (tarifaId) {
        const tarifa = tarifas.find(
          (x) => String(x.id) === String(tarifaId)
        );
        if (tarifa) {
          next.origem = tarifa.origem || next.origem;
          next.destino = tarifa.destino || next.destino;
        }
        next.valor_remessa = '';
      } else {
        const vr = coleta.valor_remessa ?? coleta.valor_declarado;
        if (vr !== null && vr !== undefined && vr !== '') {
          const num =
            typeof vr === 'number'
              ? vr
              : parseFloat(String(vr).replace(/[^\d,.-]/g, '').replace(',', '.'));
          if (!Number.isNaN(num)) {
            next.valor_remessa = formatNumberAsCurrency(num);
          }
        }
      }

      if (!canManageBoxTarifas) {
        const fixedId = String(DEFAULT_GERENTE_TARIFA_ID);
        next.tarifa_id = fixedId;
        next.tarifa_intermediacao = '';
        next.tarifa_coleta = '';
        if (pesoFormatted) {
          const t = calculateTarifa(fixedId, pesoFormatted, '', '');
          next.valor_remessa = t.valor_remessa;
          next.tarifa_intermediacao = t.tarifa_intermediacao;
          next.tarifa_coleta = t.tarifa_coleta;
        } else {
          next.valor_remessa = '';
        }
        const tf = tarifas.find((x) => String(x.id) === fixedId);
        if (tf) {
          next.origem = tf.origem || '';
          next.destino = tf.destino || '';
        }
      }

      return next;
    });
  };

  const handleColetaPrefillChange = (value) => {
    setSelectedColetaId(value);
    if (!value) return;
    const coleta = coletasForModal.find((c) => String(c.id) === String(value));
    if (coleta) applyFormPrefillFromColeta(coleta);
  };

  const handleOpenModal = async () => {
    setLoadingSequenceCodes(true);
    try {
      const codes = await loadAllCodesForSequence();
      packageCodesRef.current = codes;
      const newCode = nextPackageCodeForTipo(codes, 'caixa');
      const accessCodes = generateClientAccessCode();
      setFormData({
        codigo: newCode,
        tipo: 'caixa',
        cliente_id: '',
        cliente_nome: '',
        peso: '',
        dimensoes: {
          comprimento: DEFAULT_DIMENSAO_CM,
          largura: DEFAULT_DIMENSAO_CM,
          altura: DEFAULT_DIMENSAO_CM
        },
        valor_remessa: '',
        tarifa_id: canManageBoxTarifas ? '' : String(DEFAULT_GERENTE_TARIFA_ID),
        tarifa_intermediacao: '',
        tarifa_coleta: '',
        conteudo: '',
        observacao: '',
        status: 'pedido_criado',
        origem: '',
        destino: '',
        nome_destinatario: '',
        telefone_destinatario: '',
        pais_transito: '',
        codigo_rastreio: '',
        codigo_atualizacao: '',
        codigos_acesso: accessCodes,
        itens: [],
        nota_fiscal: null
      });
      setNotasFiscaisRemovidas([]);
      setSelectedColetaId('');
      setEditingId(null);
      setShowModal(true);
      void loadColetasForModal();
    } catch (e) {
      showWarning(
        e?.message || 'Não foi possível obter o próximo código. Tente novamente.'
      );
    } finally {
      setLoadingSequenceCodes(false);
    }
  };

  const handlePrevStep = () => {
    if (currentStep <= 1) return;
    if (!canManageBoxTarifas && currentStep === 4) {
      setCurrentStep(2);
      return;
    }
    setCurrentStep(currentStep - 1);
  };

  /** Asterisco visual para rótulos obrigatórios no wizard */
  const labelReq = (
    <span className="label-required" aria-hidden="true">
      *
    </span>
  );

  const scrollToWizardField = (fieldId) => {
    setTimeout(() => {
      const el =
        document.getElementById(fieldId) ||
        document.querySelector(`[name="${fieldId}"]`);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (el.focus) el.focus();
      }
    }, 150);
  };

  const getWizardStepValidation = (step) => {
    const invalid = {};
    const missingLabels = [];

    if (step === 1) {
      if (!formData.cliente_id) {
        invalid.cliente_id = true;
        missingLabels.push('Cliente');
      }
    } else if (step === 2) {
      if (!formData.peso || !String(formData.peso).trim()) {
        invalid.peso = true;
        missingLabels.push('Peso bruto');
      } else if (parseWeight(formData.peso) <= 0) {
        invalid.peso = true;
        missingLabels.push('Peso bruto (deve ser maior que zero)');
      }
      const dimKeys = [
        ['comprimento', 'Comprimento (cm)'],
        ['largura', 'Largura (cm)'],
        ['altura', 'Altura (cm)'],
      ];
      dimKeys.forEach(([key, label]) => {
        const v = formData.dimensoes?.[key];
        if (!v || !String(v).trim()) {
          invalid[`dim_${key}`] = true;
          missingLabels.push(label);
        } else {
          const n = parseFloat(String(v).replace(',', '.'));
          if (Number.isNaN(n) || n <= 0) {
            invalid[`dim_${key}`] = true;
            missingLabels.push(`${label} (valor inválido)`);
          }
        }
      });
    } else if (step === 3) {
      if (!formData.valor_remessa || !String(formData.valor_remessa).trim()) {
        invalid.valor_remessa = true;
        missingLabels.push('Valor da remessa');
      } else if (parseCurrency(formData.valor_remessa) <= 0) {
        invalid.valor_remessa = true;
        missingLabels.push('Valor da remessa (deve ser maior que zero)');
      }
    } else if (step === 4) {
      if (!formData.conteudo || !String(formData.conteudo).trim()) {
        invalid.conteudo = true;
        missingLabels.push('Conteúdo');
      }
    } else if (step === 5) {
      if (!formData.origem || !String(formData.origem).trim()) {
        invalid.origem = true;
        missingLabels.push('Origem');
      }
      if (!formData.destino || !String(formData.destino).trim()) {
        invalid.destino = true;
        missingLabels.push('Destino');
      }
    }

    return {
      valid: missingLabels.length === 0,
      missingLabels,
      invalid,
    };
  };

  const clearInvalidKeysForStep = (s) => {
    if (s === 1) return ['cliente_id'];
    if (s === 2) return ['peso', 'dim_comprimento', 'dim_largura', 'dim_altura'];
    if (s === 3) return ['valor_remessa'];
    if (s === 4) return ['conteudo'];
    if (s === 5) return ['origem', 'destino'];
    return [];
  };

  const handleNextStep = () => {
    const step = currentStep;
    const { valid, missingLabels, invalid } = getWizardStepValidation(step);

    if (!valid) {
      setInvalidFields((prev) => ({ ...prev, ...invalid }));
      showWarning(
        `Preencha os campos obrigatórios: ${missingLabels.join(', ')}.`
      );
      const firstKey = Object.keys(invalid)[0];
      if (firstKey === 'cliente_id') scrollToWizardField('field-cliente_id');
      else if (firstKey === 'peso') scrollToWizardField('field-peso');
      else if (firstKey === 'dim_comprimento') scrollToWizardField('field-dim-comprimento');
      else if (firstKey === 'dim_largura') scrollToWizardField('field-dim-largura');
      else if (firstKey === 'dim_altura') scrollToWizardField('field-dim-altura');
      else if (firstKey === 'valor_remessa') scrollToWizardField('field-valor_remessa');
      else if (firstKey === 'conteudo') scrollToWizardField('field-conteudo');
      else if (firstKey === 'origem') scrollToWizardField('field-origem');
      else if (firstKey === 'destino') scrollToWizardField('field-destino');
      return;
    }

    setInvalidFields((prev) => {
      const next = { ...prev };
      clearInvalidKeysForStep(step).forEach((k) => delete next[k]);
      return next;
    });

    if (step === 2 && !canManageBoxTarifas) {
      const tid = String(DEFAULT_GERENTE_TARIFA_ID);
      setFormData((prev) => {
        const tarifasCalculadas = calculateTarifa(
          tid,
          prev.peso,
          prev.tarifa_intermediacao || '',
          prev.tarifa_coleta || ''
        );
        const tf = tarifas.find((t) => String(t.id) === tid);
        let vr =
          tarifasCalculadas.valor_remessa !== undefined
            ? tarifasCalculadas.valor_remessa
            : prev.valor_remessa;
        const pesoW = parseWeight(prev.peso);
        if (
          pesoW > 0 &&
          (!String(vr).trim() || parseCurrency(vr) <= 0)
        ) {
          vr = formatNumberAsCurrency(GERENTE_VALOR_REMESA_POR_KG * pesoW);
        }
        return {
          ...prev,
          tarifa_id: tid,
          tarifa_intermediacao: tarifasCalculadas.tarifa_intermediacao,
          tarifa_coleta: tarifasCalculadas.tarifa_coleta,
          valor_remessa: vr,
          origem: tf?.origem || prev.origem,
          destino: tf?.destino || prev.destino,
        };
      });
      setCurrentStep(4);
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (let s = 1; s <= 5; s++) {
      const { valid, missingLabels, invalid } = getWizardStepValidation(s);
      if (!valid) {
        setInvalidFields((prev) => ({ ...prev, ...invalid }));
        const firstKey = Object.keys(invalid)[0];
        const goStep =
          s === 3 && !canManageBoxTarifas && firstKey === 'valor_remessa' ? 2 : s;
        setCurrentStep(goStep);
        showWarning(
          `Preencha os campos obrigatórios: ${missingLabels.join(', ')}.`
        );
        if (firstKey === 'cliente_id') scrollToWizardField('field-cliente_id');
        else if (firstKey === 'peso') scrollToWizardField('field-peso');
        else if (firstKey === 'dim_comprimento') scrollToWizardField('field-dim-comprimento');
        else if (firstKey === 'dim_largura') scrollToWizardField('field-dim-largura');
        else if (firstKey === 'dim_altura') scrollToWizardField('field-dim-altura');
        else if (firstKey === 'valor_remessa')
          scrollToWizardField(!canManageBoxTarifas ? 'field-peso' : 'field-valor_remessa');
        else if (firstKey === 'conteudo') scrollToWizardField('field-conteudo');
        else if (firstKey === 'origem') scrollToWizardField('field-origem');
        else if (firstKey === 'destino') scrollToWizardField('field-destino');
        return;
      }
    }

    try {
      setSaving(true);
      // Gerar código de acesso do cliente automaticamente se não existir
      let codigosAcesso = formData.codigos_acesso;
      if (!codigosAcesso || !codigosAcesso.cliente) {
        codigosAcesso = generateClientAccessCode();
      }

      // Garantir que codigos_acesso tem o formato correto com código do cliente
      codigosAcesso = {
        cliente: codigosAcesso?.cliente || ''
      };

      // Preparar itens com arquivos - manter estrutura com fotos para a API processar
      const itensComArquivos = (formData.itens || []).map((item) => {
        const itemData = {
          nome: item.nome || item.name || '',
          ncm: item.ncm || '',
          cfop: item.cfop || '',
          descricao: item.descricao || item.description || '',
          quantidade: item.quantidade || item.quantity || 1,
          peso: item.peso || item.weight ? parseWeight(item.peso || item.weight) : '',
          preco: item.preco || item.price || '',
          chave_acesso: item.chave_acesso || '',
          document_id: item.document_id || null,
          // Manter fotos no formato original (com file) para a API processar
          fotos: (item.fotos || []).map(photo => {
            // Se photo tem file, usar file, senão usar photo diretamente
            if (photo.file && photo.file instanceof File) {
              return { file: photo.file };
            } else if (photo instanceof File) {
              return { file: photo };
            }
            // Se não for File, pode ser uma URL (para edição), ignorar
            return null;
          }).filter(Boolean)
        };

        // Incluir ID numérico real (não ids temporários da extração NF)
        if (item.id != null && /^\d+$/.test(String(item.id))) {
          itemData.id = item.id;
        }

        return itemData;
      });

      // Processar nota fiscal - extrair apenas os arquivos File
      let notaFiscalProcessada = null;
      if (formData.nota_fiscal) {
        if (Array.isArray(formData.nota_fiscal)) {
          // Se for array, pegar apenas os arquivos File
          const files = formData.nota_fiscal
            .map(nf => nf.file || nf)
            .filter(f => f instanceof File);
          notaFiscalProcessada = files.length > 0 ? files : null;
        } else if (formData.nota_fiscal instanceof File) {
          notaFiscalProcessada = formData.nota_fiscal;
        } else if (formData.nota_fiscal.file && formData.nota_fiscal.file instanceof File) {
          notaFiscalProcessada = formData.nota_fiscal.file;
        } else if (typeof formData.nota_fiscal === 'string') {
          // Se for string (URL), manter como está para edição
          notaFiscalProcessada = formData.nota_fiscal;
        }
      }

      // Converter dados do formulário para o formato esperado pela API
      // TODOS os campos devem ser enviados, mesmo opcionais (conforme especificação)

      // Validar tipo - garantir que seja um valor válido
      // A API aceita: 'caixa', 'mala', 'pallet', 'container'
      // Se o valor for inválido ou vazio, usar 'caixa' como padrão
      const validTypes = ['caixa', 'mala', 'pallet', 'container'];
      let tipoValue = 'caixa'; // Valor padrão

      if (formData.tipo && typeof formData.tipo === 'string') {
        const tipoLower = formData.tipo.toLowerCase().trim();
        if (validTypes.includes(tipoLower)) {
          tipoValue = tipoLower;
        }
      }

      let codigoPacote = formData.codigo;
      if (!editingId) {
        const codes = await loadAllCodesForSequence();
        packageCodesRef.current = codes;
        codigoPacote = nextPackageCodeForTipo(codes, tipoValue);
      }

      let valorDeclarado = formData.valor_remessa || '';
      if (!editingId && !isBoxesAdmin) {
        const pesoNum = formData.peso ? parseWeight(formData.peso) : 0;
        const v = parseCurrency(String(valorDeclarado));
        if (!String(valorDeclarado).trim() || v <= 0) {
          if (pesoNum > 0) {
            valorDeclarado = formatNumberAsCurrency(
              GERENTE_VALOR_REMESA_POR_KG * pesoNum
            );
          }
        }
      }

      const boxData = {
        codigo_pacote: codigoPacote,
        tipo: tipoValue,
        cliente_id: formData.cliente_id || '',
        status: formData.status || 'pedido_criado',
        comprimento: formData.dimensoes?.comprimento || '',
        largura: formData.dimensoes?.largura || '',
        altura: formData.dimensoes?.altura || '',
        peso_bruto: formData.peso ? parseWeight(formData.peso) : '',
        peso_liquido: formData.peso_liquido || '',
        valor_declarado: valorDeclarado,
        tarifa_id: editingId
          ? formData.tarifa_id || ''
          : canManageBoxTarifas
            ? formData.tarifa_id || ''
            : String(DEFAULT_GERENTE_TARIFA_ID),
        tarifa_intermediacao: formData.tarifa_intermediacao || '',
        tarifa_coleta: formData.tarifa_coleta || '',
        conteudo: formData.conteudo || '',
        observacao: formData.observacao || '',
        origem: formData.origem || '',
        destino: formData.destino || '',
        nome_destinatario: formData.nome_destinatario || '',
        telefone_destinatario: formData.telefone_destinatario || '',
        pais_transito: formData.pais_transito || '',
        codigo_atualizacao: formData.codigo_atualizacao || '',
        tipo_pacote_id: formData.tipo_pacote_id || '',
        endereco_origin_id: formData.endereco_origin_id || '',
        endereco_destino_id: formData.endereco_destino_id || '',
        codigos_acesso: codigosAcesso,
        dimensoes: formData.dimensoes ? JSON.stringify(formData.dimensoes) : '',
        itens: itensComArquivos,
        nota_fiscal: notaFiscalProcessada
      };

      if (editingId) {
        if (notasFiscaisRemovidas.length > 0) {
          boxData.remover_nota_fiscal = notasFiscaisRemovidas;
        }
        const resposta = await apiService.updateBox(editingId, boxData);
        // A mensagem da API informa quantos itens entraram de fato.
        showSuccess(resposta?.message || 'Caixa/Mala atualizada com sucesso!');
      } else {
        const resposta = await apiService.createBox(boxData);
        showSuccess(resposta?.message || 'Caixa/Mala criada com sucesso!');
      }
      handleCloseModal();
      loadBoxes();
    } catch (error) {
      console.error('Erro ao salvar caixa/mala:', error);
      showWarning(error.message || 'Erro ao salvar caixa/mala');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (box) => {
    setFormData({
      ...box,
      // Mapear codigo_pacote para codigo
      codigo: box.codigo_pacote || box.codigo || '',
      // Mapear cliente.nome para cliente_nome
      cliente_nome: box.cliente?.nome || box.cliente_nome || '',
      // Mapear cliente_id
      cliente_id: box.cliente_id || box.cliente?.id || '',
      // Mapear dimensões separadas para objeto dimensoes
      dimensoes: {
        comprimento: box.comprimento || box.dimensoes?.comprimento || '',
        largura: box.largura || box.dimensoes?.largura || '',
        altura: box.altura || box.dimensoes?.altura || ''
      },
      // Mapear peso_bruto para peso
      peso: box.peso_bruto || box.peso || '',
      // Mapear valor_declarado para valor_remessa
      valor_remessa: box.valor_declarado || box.valor_remessa || '',
      // Mapear tarifa_id
      tarifa_id: box.tarifa_id || '',
      // Mapear tarifa_intermediacao e tarifa_coleta
      tarifa_intermediacao: box.tarifa_intermediacao || '',
      tarifa_coleta: box.tarifa_coleta || '',
      // Mapear origem e destino (garantir que sempre sejam strings)
      origem: box.origem || '',
      destino: box.destino || '',
      // Mapear nome_destinatario e telefone_destinatario
      nome_destinatario: box.nome_destinatario || '',
      telefone_destinatario: box.telefone_destinatario || '',
      // Mapear observacao
      observacao: box.observacao || '',
      // Garantir que codigos_acesso existe e está no formato correto
      codigos_acesso: (() => {
        let codigos = box.codigos_acesso;
        // Se for string JSON, fazer parse
        if (typeof codigos === 'string' && codigos.trim() !== '') {
          try {
            codigos = JSON.parse(codigos);
          } catch (e) {
            console.error('Erro ao fazer parse de codigos_acesso:', e);
            codigos = null;
          }
        }
        // Garantir formato padrão com código do cliente
        return codigos && typeof codigos === 'object' ? {
          cliente: codigos.cliente || ''
        } : {
          cliente: ''
        };
      })(),
      // Garantir que itens existe e formatar peso de cada item
      itens: (box.itens || []).map(item => ({
        ...item,
        peso: (() => {
          const pesoValue = item.peso || item.weight;
          if (!pesoValue) return '';
          // Se for número, multiplicar por 100 para converter para o formato da máscara
          if (typeof pesoValue === 'number') {
            return formatWeight(String(Math.round(pesoValue * 100)));
          }
          // Se for string, converter vírgula para ponto e formatar
          const pesoStr = String(pesoValue).replace(',', '.');
          const pesoNum = parseFloat(pesoStr);
          if (!isNaN(pesoNum)) {
            return formatWeight(String(Math.round(pesoNum * 100)));
          }
          return formatWeight(pesoStr);
        })()
      })),
      nota_fiscal: (() => {
        const existentes = mapNotasFiscaisExistentes(box);
        return existentes.length > 0 ? existentes : null;
      })()
    });
    setNotasFiscaisRemovidas([]);
    setEditingId(box.id);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!isBoxesAdmin) return;
    setBoxToDelete(id);
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (!isBoxesAdmin) return;
    try {
      await apiService.deleteBox(boxToDelete);
    setShowConfirmDialog(false);
    setBoxToDelete(null);
    showSuccess('Caixa/Mala excluída com sucesso!');
      loadBoxes();
    } catch (error) {
      console.error('Erro ao excluir caixa/mala:', error);
      showWarning(error.message || 'Erro ao excluir caixa/mala');
    }
  };

  // Função para converter valor formatado (R$ 200,00) para número decimal
  const parseCurrencyToNumber = (value) => {
    if (!value) return 0;
    // Remove R$, espaços, pontos (milhares) e converte vírgula para ponto
    const numberStr = value
      .toString()
      .replace(/[R$\s]/g, '')
      .replace(/\./g, '')
      .replace(',', '.')
      .trim();
    const parsed = parseFloat(numberStr);
    return isNaN(parsed) ? 0 : parsed;
  };

  const openPendenciaModal = (box) => {
    const restante = getBoxPaymentBalances(box).restante;
    const valorDefault =
      restante > 0
        ? restante
        : parseMoneyLike(box?.valor_declarado || box?.valor_remessa);
    setSelectedBoxPendencia(box);
    setPendenciaForm({
      valor_brl: valorDefault > 0 ? String(valorDefault.toFixed(2)).replace('.', ',') : '',
      moeda_local: 'XOF',
      observacao: 'Destinatário pagará na retirada',
    });
    setPendenciaPreview(null);
    setShowPendenciaModal(true);
  };

  const refreshPendenciaPreview = async (valorText, moeda) => {
    const valor = parseCurrencyToNumber(valorText);
    if (!(valor > 0)) {
      setPendenciaPreview(null);
      return;
    }
    try {
      setConvertingPendencia(true);
      const response = await apiService.convertCurrency({ amount: valor, to: moeda });
      setPendenciaPreview(response?.data || response);
    } catch (error) {
      setPendenciaPreview(null);
      showWarning(error?.message || 'Erro ao converter valor');
    } finally {
      setConvertingPendencia(false);
    }
  };

  const handleSavePendencia = async () => {
    if (!selectedBoxPendencia?.id) {
      showWarning('Caixa/Mala não selecionada');
      return;
    }
    const valor = parseCurrencyToNumber(pendenciaForm.valor_brl);
    if (!(valor > 0)) {
      showWarning('Informe o valor em Real (BRL) da pendência');
      return;
    }
    const { restante } = getBoxPaymentBalances(selectedBoxPendencia);
    if (restante > 0 && valor - restante > 0.009) {
      showWarning(
        `A pendência não pode ser maior que o restante a pagar (${displayCurrency(restante)})`
      );
      return;
    }
    try {
      setSavingPendencia(true);
      const response = await apiService.createPendenciaPagamento({
        pacote_id: selectedBoxPendencia.id,
        valor_brl: valor,
        moeda_local: pendenciaForm.moeda_local,
        observacao: pendenciaForm.observacao || undefined,
      });
      showSuccess(response?.message || 'Pendência registrada para pagamento na retirada');
      setShowPendenciaModal(false);
      setSelectedBoxPendencia(null);
      loadBoxes();
    } catch (error) {
      showWarning(error?.message || 'Erro ao registrar pendência');
    } finally {
      setSavingPendencia(false);
    }
  };

  const handleRegisterPayment = async () => {
    if (!boxPayment.valor || !boxPayment.data) {
      showWarning('Preencha todos os campos obrigatórios do pagamento');
      return;
    }

    if (!selectedBoxForPayment?.id) {
      showWarning('Caixa/Mala não selecionada');
      return;
    }

    const comprovantesFiles = (boxPayment.comprovantes || [])
      .map((c) => (c instanceof File ? c : c?.file))
      .filter((f) => f instanceof File);

    if (comprovantesFiles.length === 0) {
      showWarning('Anexe ao menos um comprovante de pagamento');
      return;
    }

    try {
      const valorNumerico = parseCurrencyToNumber(boxPayment.valor);

      if (valorNumerico <= 0) {
        showWarning('O valor do pagamento deve ser maior que zero');
        return;
      }

      const { restante } = getBoxPaymentBalances(selectedBoxForPayment);
      if (restante > 0 && valorNumerico - restante > 0.009) {
        showWarning(`O valor não pode ser maior que o restante (${displayCurrency(restante)})`);
        return;
      }

      const valorFormatado = valorNumerico.toFixed(2);

      const paymentData = {
        pacote_id: selectedBoxForPayment.id,
        valor: valorFormatado,
        metodo_pagamento_id: null,
        metodo_pagamento: boxPayment.metodo || 'dinheiro',
        moeda_id: null,
        data_pagamento: boxPayment.data || undefined,
        comprovantes: comprovantesFiles,
      };

      const result = await apiService.createPayment(paymentData);
      const msg = result?.message
        || `Pagamento de ${formatCurrency(boxPayment.valor)} registrado com sucesso para caixa/mala ${selectedBoxForPayment?.codigo || selectedBoxForPayment?.codigo_pacote || ''}`;
      showSuccess(msg);

      await loadBoxes();

      setShowPaymentModal(false);
      setBoxPayment(emptyBoxPayment());
      setSelectedBoxForPayment(null);
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      showWarning(error?.message || 'Erro ao registrar pagamento. Tente novamente.');
    }
  };

  const handleCancelPayment = async () => {
    if (!cancelJustification || cancelJustification.trim().length < 10) {
      showWarning('A justificativa deve ter no mínimo 10 caracteres');
      return;
    }

    if (!selectedBoxForPayment?.id) {
      showWarning('Caixa/Mala não selecionada');
      return;
    }

    try {
      await apiService.cancelPayment({
        pacote_id: selectedBoxForPayment.id,
        justificativa_cancelamento: cancelJustification.trim()
      });

      showSuccess('Pagamento cancelado com sucesso!');

      // Recarregar boxes para atualizar status
      await loadBoxes();

      // Fechar modais
      setShowCancelPaymentModal(false);
      setShowViewPaymentModal(false);
      setCancelJustification('');
      setSelectedPayment(null);
      setSelectedBoxForPayment(null);
    } catch (error) {
      console.error('Erro ao cancelar pagamento:', error);
      showWarning(error?.message || 'Erro ao cancelar pagamento. Tente novamente.');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentStep(1);
    setFormData({
      codigo: '',
      tipo: 'caixa',
      cliente_id: '',
      cliente_nome: '',
      peso: '',
      dimensoes: {
        comprimento: DEFAULT_DIMENSAO_CM,
        largura: DEFAULT_DIMENSAO_CM,
        altura: DEFAULT_DIMENSAO_CM
      },
      valor_remessa: '',
      tarifa_id: '',
      tarifa_intermediacao: '',
      tarifa_coleta: '',
      conteudo: '',
      observacao: '',
      status: 'pedido_criado',
      origem: '',
      destino: '',
      nome_destinatario: '',
      telefone_destinatario: '',
      pais_transito: '',
      codigo_rastreio: '',
      codigo_atualizacao: '',
      codigos_acesso: {
        cliente: ''
      },
      itens: [],
      nota_fiscal: null
    });
    setNotasFiscaisRemovidas([]);
    setEditingId(null);
    setSelectedColetaId('');
    setCurrentItem({
      nome: '',
      ncm: '',
      cfop: '',
      descricao: '',
      peso: '',
      preco: '',
      quantidade: 1,
      unidade: '',
      fotos: []
    });
  };

  const handleAddItem = () => {
    // Validar campos obrigatórios
    if (!currentItem.nome) {
      validateAndHighlightField('item-nome', 'Preencha o nome do item');
      return;
    }
    if (!currentItem.peso) {
      validateAndHighlightField('item-peso', 'Preencha o peso do item');
      return;
    }
    if (!currentItem.preco) {
      validateAndHighlightField('item-preco', 'Preencha o preço do item');
      return;
    }

    // Limpar validações ao adicionar item com sucesso
    setInvalidFields({});

    // Processar fotos antes de adicionar o item
    const processedFotos = (currentItem.fotos || []).map(photo => {
      // Se a foto tem um file, manter o file
      // Se tem preview mas não file, tentar manter o preview
      // Remover referências a blob URLs que podem ser inválidas
      if (photo.file && photo.file instanceof File) {
        return {
          id: photo.id || createLocalId('foto'),
          file: photo.file,
          name: photo.name || photo.file.name,
          preview: photo.preview // Manter preview se existir
        };
      }
      // Se não tem file, mas tem preview válido, manter apenas o preview
      if (photo.preview && typeof photo.preview === 'string') {
        return {
          id: photo.id || createLocalId('foto'),
          name: photo.name || 'Foto',
          preview: photo.preview
        };
      }
      return null;
    }).filter(photo => photo !== null);

    const newItem = {
      id: createLocalId('item'),
      nome: currentItem.nome || '',
      ncm: currentItem.ncm || '',
      cfop: currentItem.cfop || '',
      descricao: currentItem.descricao || '',
      peso: currentItem.peso || '',
      preco: currentItem.preco || '',
      quantidade: parseFloat(String(currentItem.quantidade).replace(',', '.')) || 1,
      unidade: currentItem.unidade || '',
      fotos: processedFotos
    };

    // Forma funcional: a extração da NF também escreve em itens e não pode
    // ser sobrescrita por um estado antigo capturado neste handler.
    setFormData((prev) => ({
      ...prev,
      itens: [...(prev.itens || []), newItem]
    }));

    setCurrentItem({
      nome: '',
      ncm: '',
      cfop: '',
      descricao: '',
      peso: '',
      preco: '',
      quantidade: 1,
      unidade: '',
      fotos: []
    });
    setNcmSuggestions([]);
    setShowNcmDropdown(false);
  };

  // Função para comprimir imagem
  const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.6) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Redimensionar se necessário
          if (width > maxWidth || height > maxHeight) {
            if (width > height) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            } else {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Converter para blob comprimido
          canvas.toBlob((blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          }, 'image/jpeg', quality);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Funções para câmera
  const handleOpenCamera = (forItem = false, forNotaFiscal = false, forComprovante = false) => {
    setCameraForItem(forItem);
    setCameraForNotaFiscal(forNotaFiscal);
    setCameraForComprovante(forComprovante);
    setShowCameraModal(true);
  };

  const handleCloseCamera = () => {
    setShowCameraModal(false);
    if (videoRef.current) {
      const stream = videoRef.current.srcObject;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      videoRef.current.srcObject = null;
    }
  };

  const startCamera = async () => {
    try {
      // Parar stream anterior se existir
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        stream.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: cameraFacingMode }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      showWarning('Não foi possível acessar a câmera. Verifique as permissões.');
      handleCloseCamera();
    }
  };

  const toggleCamera = async () => {
    setCameraFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    // A câmera será reiniciada no useEffect quando cameraFacingMode mudar
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Ajustar dimensões do canvas para o vídeo
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Desenhar frame do vídeo no canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Converter canvas para blob e depois para File (com compressão)
    canvas.toBlob(async (blob) => {
      if (blob) {
        const originalFile = new File([blob], `foto_${Date.now()}.jpg`, { type: 'image/jpeg' });

        // Comprimir a imagem
        const compressedFile = await compressImage(originalFile, 1920, 1920, 0.6);

        const uploadedFile = {
          id: createLocalId('arquivo'),
          file: compressedFile,
          preview: URL.createObjectURL(compressedFile),
          name: compressedFile.name
        };

        if (cameraForItem) {
          // Adicionar foto ao item atual
          setCurrentItem({ ...currentItem, fotos: [...(currentItem.fotos || []), uploadedFile] });
        } else if (cameraForNotaFiscal) {
          const notaFiscalFile = {
            id: createLocalId('arquivo'),
            file: compressedFile,
            preview: URL.createObjectURL(compressedFile),
            name: compressedFile.name
          };
          await handleNotaFiscalUpload([notaFiscalFile]);
          handleCloseCamera();
          return;
        } else if (cameraForComprovante) {
          setBoxPayment((prev) => ({
            ...prev,
            comprovantes: [
              ...(prev.comprovantes || []),
              { file: compressedFile, name: compressedFile.name, preview: URL.createObjectURL(compressedFile) },
            ],
          }));
        }

        showSuccess('Foto capturada e comprimida com sucesso!');
        handleCloseCamera();
      }
    }, 'image/jpeg', 0.9);
  };

  useEffect(() => {
    if (showCameraModal && videoRef.current) {
      startCamera();
    }
    return () => {
      if (videoRef.current) {
        const stream = videoRef.current.srcObject;
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
      }
    };
  }, [showCameraModal, cameraFacingMode]);



  /**
   * NFs já salvas viram itens com o id do documento, para poderem ser abertas
   * e excluídas individualmente na edição.
   */
  const mapNotasFiscaisExistentes = (box) => {
    const docs = Array.isArray(box?.notas_fiscais) ? box.notas_fiscais : [];
    if (docs.length > 0) {
      return docs.map((doc, index) => ({
        id: `nf-doc-${doc.id}`,
        documentId: doc.id,
        name: doc.nome_arquivo || doc.descricao || `Nota Fiscal ${index + 1}`,
        url: doc.arquivo_url || null,
        preview: doc.arquivo_url || null,
        chave_acesso: doc.chave_acesso || null
      }));
    }

    // Remessas antigas só têm as URLs, sem o id do documento.
    const urls = Array.isArray(box?.nota_fiscal_url)
      ? box.nota_fiscal_url
      : box?.nota_fiscal_url
        ? [box.nota_fiscal_url]
        : [];

    return urls
      .filter((url) => typeof url === 'string' && url.trim() !== '')
      .map((url, index) => ({
        id: `nf-url-${index}`,
        name: decodeURIComponent(url.split('/').pop() || `Nota Fiscal ${index + 1}`),
        url,
        preview: url
      }));
  };

  const mapExtractedNfItems = (itens, chave) =>
    (Array.isArray(itens) ? itens : []).map((item, index) => {
      const descricao = item.descricao || item.nome || `Item NF ${index + 1}`;
      const ncmExtraido = String(item.ncm || '').replace(/\D/g, '');
      const ncmSugerido =
        ncmExtraido.length === 8
          ? ncmExtraido
          : String(searchNcmByProductName(descricao)[0] || '').replace(/\D/g, '');

      return {
        id: createLocalId(`nf-${index}`),
        nome: item.nome || descricao,
        descricao,
        // A leitura da coluna NCM/SH tem prioridade; se falhar, usa a mesma
        // sugestão automática disponível no preenchimento manual dos itens.
        ncm: ncmSugerido,
        cfop: item.cfop || '',
        unidade: item.unidade || '',
        peso:
          item.peso_unitario != null
            ? String(item.peso_unitario)
            : item.peso != null
              ? String(item.peso)
              : '',
        preco:
          item.valor_unitario != null
            ? String(item.valor_unitario)
            : item.preco != null
              ? String(item.preco)
              : '',
        quantidade: item.quantidade != null ? Number(item.quantidade) || 1 : 1,
        fotos: [],
        from_nf: true,
        chave_acesso: item.chave_acesso || chave || '',
        valor_unitario_tributavel: item.valor_unitario_tributavel ?? null
      };
    });

  const extractItemsFromNfFiles = async (fileObjects) => {
    const files = (Array.isArray(fileObjects) ? fileObjects : [fileObjects])
      .map((f) => f?.file || f)
      .filter((f) => f instanceof File);

    if (files.length === 0) return { itens: [], chave: null };

    setExtractingNf(true);
    try {
      const allItens = [];
      let chave = null;
      for (const file of files) {
        try {
          const response = await apiService.extractNotaFiscal(file);
          const data = response?.data || {};
          const chaveArquivo = data.chave_acesso || null;
          if (chaveArquivo) chave = chaveArquivo;
          if (Array.isArray(data.itens) && data.itens.length > 0) {
            allItens.push(...mapExtractedNfItems(data.itens, chaveArquivo));
          }
        } catch (err) {
          console.error('Erro ao extrair NF:', err);
          showWarning(err.message || `Não foi possível extrair dados de ${file.name}`);
        }
      }
      return { itens: allItens, chave };
    } finally {
      setExtractingNf(false);
    }
  };

  const handleNotaFiscalUpload = async (files) => {
    let uploadedFiles;
    if (Array.isArray(files) && files.length > 0 && files[0].id) {
      uploadedFiles = files;
    } else {
      uploadedFiles = Array.from(files).map((file) => ({
        id: createLocalId('arquivo'),
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        name: file.name
      }));
    }

    setFormData((prev) => ({
      ...prev,
      nota_fiscal: prev.nota_fiscal
        ? [
            ...(Array.isArray(prev.nota_fiscal) ? prev.nota_fiscal : [prev.nota_fiscal]),
            ...uploadedFiles
          ]
        : uploadedFiles
    }));

    const { itens: extracted, chave } = await extractItemsFromNfFiles(uploadedFiles);
    if (extracted.length > 0) {
      setFormData((prev) => {
        const existing = Array.isArray(prev.itens) ? prev.itens : [];
        return {
          ...prev,
          nf_chave_acesso: chave || prev.nf_chave_acesso || '',
          itens: [...existing, ...extracted]
        };
      });
      showSuccess(
        `${extracted.length} ite${extracted.length === 1 ? 'm' : 'ns'} extraído${extracted.length === 1 ? '' : 's'} da nota fiscal. Confira na etapa Itens.`
      );
    } else {
      showWarning(
        'Nota fiscal anexada. Nenhum item identificado automaticamente — preencha manualmente na etapa Itens.'
      );
    }
  };

  const handleRemoveNotaFiscal = (index) => {
    const lista = Array.isArray(formData.nota_fiscal)
      ? formData.nota_fiscal
      : formData.nota_fiscal
        ? [formData.nota_fiscal]
        : [];

    const removida = lista[index];
    if (!removida) return;

    // NF já salva: o backend precisa receber o id para apagar de verdade.
    if (removida.documentId) {
      setNotasFiscaisRemovidas((prev) =>
        prev.includes(removida.documentId) ? prev : [...prev, removida.documentId]
      );
    }
    if (removida.preview && removida.preview.startsWith('blob:')) {
      URL.revokeObjectURL(removida.preview);
    }

    const restante = lista.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      nota_fiscal: restante.length > 0 ? restante : null
    }));
  };

  const getStatusIconEl = (status) => {
    if (!status) status = 'pedido_criado';
    const statusInfo = getStatusInfo(status);
    const statusColor = getStatusColor(status);
    const iconName = getStatusIcon(status);
    const IconComponent = HiIcons[iconName] || HiIcons.HiClock;
    return (
      <span
        title={statusInfo.label}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: statusColor,
          cursor: 'default'
        }}
      >
        <IconComponent size={20} />
      </span>
    );
  };

  // Paginação é feita na API; boxes já contém apenas os itens da página atual.
  const totalPages = lastPage;
  const startIndex = totalBoxes > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endIndex = Math.min(currentPage * itemsPerPage, totalBoxes);
  const invoiceRowClassByKey = useMemo(
    () => buildInvoiceRowClassMap(boxes),
    [boxes]
  );
  const destinoTextClassByKey = useMemo(
    () => buildDestinoTextClassMap(boxes),
    [boxes]
  );
  const codigoClienteRecipient = selectedBoxRecipient
    ? getClienteCodigoFromBox(selectedBoxRecipient)
    : '';
  const pendenciaRetiradaRecipient =
    selectedBoxRecipient?.pendencia_pagamento?.status === 'pendente'
      ? selectedBoxRecipient.pendencia_pagamento
      : null;
  const recipientDestino =
    normalizePackageDestino(selectedBoxRecipient) || 'Bissau';
  const recipientPickupLocation = getPickupLocationText(recipientDestino);
  const recipientPickupContact = getPickupContact(recipientDestino);

  const getRecipientPublicUrl = () => {
    if (!selectedBoxRecipient || !codigoClienteRecipient) return '';
    const params = new URLSearchParams({
      destinatario: selectedBoxRecipient.nome_destinatario || 'Destinatário',
      codigo: codigoClienteRecipient,
      destino: normalizePackageDestino(selectedBoxRecipient) || 'Bissau',
    });
    if (recipientCobrancaId) {
      params.set('cobranca', recipientCobrancaId);
    }
    if (pendenciaRetiradaRecipient) {
      params.set('pagar_local', String(pendenciaRetiradaRecipient.valor_local ?? ''));
      params.set('moeda_local', String(pendenciaRetiradaRecipient.moeda_local || ''));
      if (pendenciaRetiradaRecipient.valor_brl != null) {
        params.set('pagar_brl', String(pendenciaRetiradaRecipient.valor_brl));
      }
    }
    return `${window.location.origin}/retirada?${params.toString()}`;
  };

  const getClientePhoneFromBox = (box) => {
    if (!box) return '';
    const clientId = box.cliente_id || box.cliente?.id;
    const fromList = clientId
      ? clients.find((c) => String(c.id) === String(clientId))
      : null;
    return (
      box.cliente?.telefone ||
      box.cliente_telefone ||
      box.telefone_cliente ||
      fromList?.telefone ||
      fromList?.phone ||
      ''
    );
  };

  const handleSendRecipientPickupInfo = () => {
    if (!codigoClienteRecipient) {
      showWarning('Esta carga não possui código do cliente para gerar o QR Code.');
      return;
    }

    const rawPhone = String(getClientePhoneFromBox(selectedBoxRecipient) || '').replace(
      /\D/g,
      ''
    );
    if (!rawPhone) {
      showWarning('Cliente sem telefone cadastrado. Cadastre o telefone do cliente para enviar no WhatsApp.');
      return;
    }

    const publicUrl = getRecipientPublicUrl();
    const clientName =
      selectedBoxRecipient.cliente?.nome ||
      selectedBoxRecipient.cliente_nome ||
      selectedBoxRecipient.cliente?.name ||
      clients.find(
        (c) =>
          String(c.id) ===
          String(selectedBoxRecipient.cliente_id || selectedBoxRecipient.cliente?.id)
      )?.nome ||
      'cliente';
    const messageLines = [
      `Olá, ${clientName}!`,
      '',
      'Segue o link com o QR Code e as informações de retirada da sua carga:',
      publicUrl,
      '',
    ];
    if (pendenciaRetiradaRecipient) {
      messageLines.push(
        `Valor a pagar na retirada: ${formatMoneyWithFlag(
          pendenciaRetiradaRecipient.valor_local,
          pendenciaRetiradaRecipient.moeda_local
        )}`,
        ''
      );
    }
    messageLines.push(
      'Guarde este link e apresente o QR Code no momento da retirada.',
      'SendingTo Express'
    );
    const message = messageLines.join('\n');

    if (!openWhatsAppApp(rawPhone, message)) {
      showWarning('Telefone do cliente inválido. Inclua o código do país.');
      return;
    }

    showSuccess('Informações de retirada abertas no WhatsApp do cliente.');
  };

  return (
    <div className="page-container">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>Caixas / Malas</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Caixas / Malas</h1>
          <p>Gerencie caixas e malas para envio</p>
        </div>
        {canCreateEditBoxes && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleOpenModal}
            disabled={loadingSequenceCodes}
          >
            <HiPlus size={20} />
            {loadingSequenceCodes ? 'Carregando…' : 'Nova Caixa/Mala'}
          </button>
        )}
      </div>

      <div className="page-actions boxes-page-filters">
        <div className="search-box">
          <HiSearch className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Código, cliente..."
            value={boxListFilters.search}
            onChange={(e) => {
              setBoxListFilters((prev) => ({ ...prev, search: e.target.value }));
              setCurrentPage(1);
            }}
          />
        </div>
        <div className="search-box boxes-filter-invoice">
          <HiDocument className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Invoice (código)"
            value={boxListFilters.invoice}
            onChange={(e) => {
              setBoxListFilters((prev) => ({ ...prev, invoice: e.target.value }));
              setCurrentPage(1);
            }}
          />
        </div>
        <div className="search-box boxes-filter-destino">
          <HiLocationMarker className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Destino"
            value={boxListFilters.destino}
            onChange={(e) => {
              setBoxListFilters((prev) => ({ ...prev, destino: e.target.value }));
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <SkeletonTable rows={8} columns={canCreateEditBoxes ? 13 : 12} />
        ) : !Array.isArray(boxes) || boxes.length === 0 ? (
          <div className="empty-state">
            <p>
              {boxListFilters.search ||
              boxListFilters.invoice ||
              boxListFilters.destino
                ? 'Nenhuma caixa/mala encontrada'
                : 'Nenhuma caixa/mala cadastrada'}
            </p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Código</th>
                    <th>Tipo</th>
                    <th>Cliente</th>
                    <th>Invoice</th>
                    <th>Criado</th>
                    <th>Destino</th>
                    <th>Peso</th>
                    <th>Dimensões</th>
                    {canCreateEditBoxes && <th>Valor</th>}
                    <th>Status</th>
                    <th>NF</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {boxes.map((box, index) => {
                    const invKey = getBoxInvoiceKey(box);
                    const invRowClass = invKey ? invoiceRowClassByKey.get(invKey) : '';
                    const destinoKey = getDestinoGroupKey(box);
                    const destinoTextClass =
                      destinoTextClassByKey.get(destinoKey) || 'boxes-destino-text--empty';
                    const destinoDisplay =
                      box.destino && String(box.destino).trim() !== ''
                        ? String(box.destino).trim()
                        : '—';
                    return (
                    <tr key={box.id} className={invRowClass || undefined}>
                      <td>{startIndex + index}</td>
                      <td><strong>{box.codigo || box.codigo_pacote || 'N/A'}</strong></td>
                      <td>{box.tipo === 'caixa' ? '📦 Caixa' : '🧳 Mala'}</td>
                      <td>
                        {box.cliente_nome ||
                         box.cliente?.nome ||
                         box.cliente?.name ||
                         box.cliente_name ||
                         (box.cliente && typeof box.cliente === 'string' ? box.cliente : null) ||
                         'N/A'}
                      </td>
                      <td className="boxes-table-cell-invoice">
                        {getBoxInvoiceLabel(box) ? (
                          <span
                            className="boxes-table-invoice-badge"
                            title={invKey ? `Invoice #${invKey}` : undefined}
                          >
                            {getBoxInvoiceLabel(box)}
                          </span>
                        ) : (
                          <span className="boxes-table-invoice-empty">—</span>
                        )}
                      </td>
                      <td className="boxes-table-cell-datetime">
                        {formatBoxCreatedAt(box.created_at)}
                      </td>
                      <td className="boxes-table-cell-destino">
                        <strong className={destinoTextClass}>{destinoDisplay}</strong>
                      </td>
                      <td>{box.peso || box.peso_bruto || 'N/A'}</td>
                      <td>
                        {(box.dimensoes?.comprimento || box.comprimento) ? `${box.dimensoes?.comprimento || box.comprimento}cm` : '-'} x {(box.dimensoes?.largura || box.largura) ? `${box.dimensoes?.largura || box.largura}cm` : '-'} x {(box.dimensoes?.altura || box.altura) ? `${box.dimensoes?.altura || box.altura}cm` : '-'}
                      </td>
                      {canCreateEditBoxes && (
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <strong>{displayCurrency(box.valor_remessa || box.valor_declarado)}</strong>
                            {box.status_pagamento && (
                              <span style={{
                                fontSize: '0.75rem',
                                color: box.status_pagamento?.toLowerCase() === 'pago' ? '#10b981' :
                                       box.status_pagamento?.toLowerCase() === 'incompleto' ? '#f97316' :
                                       box.status_pagamento?.toLowerCase() === 'pendente' ? '#f59e0b' :
                                       box.status_pagamento?.toLowerCase() === 'cancelado' ? '#ef4444' : '#6b7280',
                                fontWeight: 500
                              }}>
                                {box.status_pagamento?.toLowerCase() === 'pago' ? '✓ Pago' :
                                 box.status_pagamento?.toLowerCase() === 'incompleto' ? `◑ Incompleto${box.valor_pago != null ? ` · pago ${displayCurrency(box.valor_pago)}` : ''}${box.valor_restante != null ? ` · resta ${displayCurrency(box.valor_restante)}` : ''}` :
                                 box.status_pagamento?.toLowerCase() === 'pendente' ? '○ Pendente' :
                                 box.status_pagamento?.toLowerCase() === 'cancelado' ? '✕ Cancelado' :
                                 box.status_pagamento}
                              </span>
                            )}
                            {box.pendencia_pagamento?.status === 'pendente' && (
                              <span style={{
                                fontSize: '0.72rem',
                                color: '#b45309',
                                fontWeight: 650,
                              }}>
                                ⚠ Pagar na retirada ·{' '}
                                {formatMoneyWithFlag(
                                  box.pendencia_pagamento.valor_local,
                                  box.pendencia_pagamento.moeda_local
                                )}
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      <td>{getStatusIconEl(box.status)}</td>
                      <td>
                        {(() => {
                          let notaFiscalUrl = null;
                          if (Array.isArray(box.nota_fiscal_url) && box.nota_fiscal_url.length > 0) {
                            notaFiscalUrl = box.nota_fiscal_url[0];
                          } else if (typeof box.nota_fiscal_url === 'string' && box.nota_fiscal_url.trim() !== '') {
                            notaFiscalUrl = box.nota_fiscal_url;
                          } else if (box.nota_fiscal && typeof box.nota_fiscal === 'string' && box.nota_fiscal.trim() !== '') {
                            notaFiscalUrl = box.nota_fiscal;
                          }

                          const hasNotaFiscal = notaFiscalUrl && typeof notaFiscalUrl === 'string' && notaFiscalUrl.trim() !== '';

                          if (!hasNotaFiscal) {
                            return (
                              <span
                                className="btn-icon"
                                title="Nota Fiscal não disponível"
                                style={{ color: '#9ca3af', cursor: 'not-allowed', opacity: 0.5 }}
                              >
                                <HiEye size={18} />
                              </span>
                            );
                          }

                          const isPDF = typeof notaFiscalUrl === 'string' && (
                            notaFiscalUrl.match(/\.(pdf)$/i) ||
                            notaFiscalUrl.toLowerCase().includes('application/pdf') ||
                            notaFiscalUrl.toLowerCase().includes('pdf')
                          );

                          const isImage = typeof notaFiscalUrl === 'string' && !isPDF && (
                            notaFiscalUrl.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i) ||
                            notaFiscalUrl.match(/^data:image\//) ||
                            notaFiscalUrl.toLowerCase().includes('image/') ||
                            notaFiscalUrl.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)\?/i)
                          );

                          if (isImage) {
                            return (
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => {
                                  setSelectedNotaFiscal(notaFiscalUrl);
                                  setShowNotaFiscalModal(true);
                                }}
                                title="Ver Nota Fiscal"
                                style={{ color: '#3b82f6' }}
                              >
                                <HiEye size={18} />
                              </button>
                            );
                          }

                          return (
                            <a
                              href={notaFiscalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-icon"
                              title="Ver Nota Fiscal"
                              style={{ color: '#3b82f6', textDecoration: 'none' }}
                            >
                              <HiEye size={18} />
                            </a>
                          );
                        })()}
                      </td>
                      <td>
                        <div className="actions">
                          {canCreateEditBoxes && (
                            <button
                              type="button"
                              className="btn-icon btn-edit"
                              onClick={() => handleEdit(box)}
                              title="Editar"
                            >
                              <HiPencil size={18} />
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => navigate(`/labels?box=${box.id}`)}
                            title="Gerar Etiqueta"
                          >
                            <HiPrinter size={18} />
                          </button>
                          <div className="actions-menu-wrapper" style={{ position: 'relative' }}>
                            <button
                              type="button"
                              className="btn-icon btn-menu-toggle"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openMenuId === box.id) {
                                  setOpenMenuId(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const menuHeight = 300;
                                  const menuWidth = 200;
                                  const viewportHeight = window.innerHeight;
                                  const viewportWidth = window.innerWidth;
                                  let top = rect.bottom + 5;
                                  if (top + menuHeight > viewportHeight) {
                                    top = rect.top - menuHeight - 5;
                                    if (top < 0) top = Math.max(10, (viewportHeight - menuHeight) / 2);
                                  }
                                  let right = viewportWidth - rect.right;
                                  if (right + menuWidth > viewportWidth) {
                                    right = viewportWidth - rect.left - menuWidth;
                                    if (right < 0) right = (viewportWidth - menuWidth) / 2;
                                  }
                                  setMenuPosition({ top, right });
                                  setOpenMenuId(box.id);
                                }
                              }}
                              title="Mais opções"
                            >
                              <HiDotsVertical size={18} />
                            </button>
                            {openMenuId === box.id && (
                              <>
                                <div
                                  className="actions-dropdown-overlay"
                                  onClick={() => setOpenMenuId(null)}
                                />
                                <div
                                  className="actions-dropdown-menu"
                                  style={{
                                    top: `${menuPosition.top}px`,
                                    right: `${menuPosition.right}px`,
                                    transform: 'none'
                                  }}
                                >
                                <button
                                  className="dropdown-menu-item"
                                  onClick={async () => {
                                    setOpenMenuId(null);
                                    try {
                                      const response = await apiService.getBoxCodes(box.id);
                                      const codigos = response?.data || response || {};
                                      const cliente =
                                        typeof codigos === 'object'
                                          ? codigos.cliente || ''
                                          : '';
                                      setSelectedBoxCodes({
                                        ...box,
                                        codigo: box.codigo_pacote || box.codigo || '',
                                        codigos_acesso: { cliente },
                                      });
                                      setShowCodesModal(true);
                                      if (cliente && !box.codigos_acesso?.cliente) {
                                        loadBoxes();
                                      }
                                    } catch (error) {
                                      showWarning(
                                        error?.message || 'Erro ao carregar códigos de acesso'
                                      );
                                    }
                                  }}
                                >
                                  <HiKey size={16} />
                                  <span>Códigos de Acesso</span>
                          </button>
                          <button
                                  className="dropdown-menu-item"
                                  onClick={() => {
                                    setSelectedBoxItems(box);
                                    setShowItemsModal(true);
                                    setOpenMenuId(null);
                                  }}
                                >
                                  <HiCollection size={16} />
                                  <span>Listar Itens</span>
                                </button>
                                {box.entregas && Array.isArray(box.entregas) && box.entregas.length > 0 && (
                                  <button
                                    className="dropdown-menu-item"
                                    onClick={() => {
                                      setSelectedBoxDelivery(box);
                                      setShowDeliveryModal(true);
                                      setOpenMenuId(null);
                                    }}
                                  >
                                    <HiLocationMarker size={16} />
                                    <span>Dados da Entrega</span>
                                  </button>
                                )}
                                <button
                                  className="dropdown-menu-item"
                                  onClick={async () => {
                                    setSelectedBoxRecipient(box);
                                    setRecipientCobrancaId('');
                                    setShowRecipientModal(true);
                                    setOpenMenuId(null);
                                    try {
                                      const res = await apiService.getCobrancasList({
                                        pacote_id: box.id,
                                        per_page: 10,
                                      });
                                      const page = res?.data || res;
                                      const list = Array.isArray(page?.data)
                                        ? page.data
                                        : Array.isArray(page)
                                          ? page
                                          : [];
                                      const active = list.find(
                                        (c) => c && c.status && c.status !== 'cancelada'
                                      );
                                      if (active?.id) {
                                        setRecipientCobrancaId(String(active.id));
                                      }
                                    } catch (e) {
                                      // Sem cobrança vinculada — URL segue só com código do cliente
                                    }
                                  }}
                                >
                                  <HiEye size={16} />
                                  <span>Ver Destinatário</span>
                                </button>
                                <button
                                  className="dropdown-menu-item"
                                  onClick={async () => {
                                    setOpenMenuId(null);

                                    const statusPagamento = box.status_pagamento ? box.status_pagamento.toLowerCase() : '';
                                    if (statusPagamento === 'pago' || statusPagamento === 'cancelado') {
                                      try {
                                        const payment = await apiService.getPaymentByPackage(box.id);
                                        if (payment && payment.data) {
                                          setSelectedPayment(payment.data);
                                          setSelectedBoxForPayment(box);
                                          setShowViewPaymentModal(true);
                                        } else {
                                          openRegisterPaymentModal(box);
                                        }
                                      } catch (error) {
                                        console.error('Erro ao buscar pagamento:', error);
                                        openRegisterPaymentModal(box);
                                      }
                                    } else {
                                      openRegisterPaymentModal(box);
                                    }
                                  }}
                                >
                                  {box.status_pagamento && (box.status_pagamento.toLowerCase() === 'pago' || box.status_pagamento.toLowerCase() === 'cancelado') ? (
                                    <>
                                      <HiEye size={16} />
                                      <span>Ver Pagamento</span>
                                    </>
                                  ) : (
                                    <>
                                      <HiCash size={16} />
                                      <span>
                                        {box.status_pagamento?.toLowerCase() === 'incompleto'
                                          ? 'Registrar outro pagamento'
                                          : 'Registrar Pagamento'}
                                      </span>
                                    </>
                                  )}
                                </button>
                                {box.status_pagamento?.toLowerCase() === 'incompleto' && (
                                  <button
                                    className="dropdown-menu-item"
                                    onClick={async () => {
                                      setOpenMenuId(null);
                                      try {
                                        const payment = await apiService.getPaymentByPackage(box.id);
                                        if (payment && payment.data) {
                                          setSelectedPayment(payment.data);
                                          setSelectedBoxForPayment(box);
                                          setShowViewPaymentModal(true);
                                        }
                                      } catch (error) {
                                        console.error('Erro ao buscar pagamento:', error);
                                        showWarning(error?.message || 'Erro ao buscar pagamentos');
                                      }
                                    }}
                                  >
                                    <HiEye size={16} />
                                    <span>Ver pagamentos</span>
                                  </button>
                                )}
                                {(!box.pendencia_pagamento ||
                                  box.pendencia_pagamento.status !== 'pendente') && (
                                  <button
                                    className="dropdown-menu-item"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      openPendenciaModal(box);
                                    }}
                                  >
                                    <HiExclamationCircle size={16} />
                                    <span>Reporte Pendência</span>
                                  </button>
                                )}
                                {box.pendencia_pagamento?.status === 'pendente' && (
                                  <button
                                    className="dropdown-menu-item"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setSelectedBoxPendencia(box);
                                      setShowPendenciaModal(true);
                                      setPendenciaPreview({
                                        amount_brl: box.pendencia_pagamento.valor_brl,
                                        currency: box.pendencia_pagamento.moeda_local,
                                        amount_converted: box.pendencia_pagamento.valor_local,
                                        rate: box.pendencia_pagamento.taxa_cambio,
                                        date: box.pendencia_pagamento.data_cotacao,
                                        readonly: true,
                                      });
                                      setPendenciaForm({
                                        valor_brl: String(box.pendencia_pagamento.valor_brl),
                                        moeda_local: box.pendencia_pagamento.moeda_local || 'XOF',
                                        observacao: box.pendencia_pagamento.observacao || '',
                                      });
                                    }}
                                  >
                                    <HiExclamationCircle size={16} />
                                    <span>Ver pendência retirada</span>
                                  </button>
                                )}
                                <div className="dropdown-divider"></div>
                                {isBoxesAdmin && (
                                  <button
                                    className="dropdown-menu-item danger"
                                    onClick={() => {
                                      handleDelete(box.id);
                                      setOpenMenuId(null);
                                    }}
                                  >
                                    <HiTrash size={16} />
                                    <span>Excluir</span>
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>

            <div className="table-footer">
              <div className="pagination-info">
                {totalBoxes > 0 ? (
                  <span>
                    Mostrando {startIndex} a {endIndex} de {totalBoxes} registro{totalBoxes !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span>Nenhum registro encontrado</span>
                )}
              </div>
              <div className="items-per-page">
                <span>Mostrar:</span>
                <Select
                  value={itemsPerPage}
                  onChange={(e) => {
                    const perPage = Number(e.target.value);
                    setItemsPerPage(perPage);
                    setCurrentPage(1);
                    loadBoxes({ page: 1, per_page: perPage });
                  }}
                  className="select-sm"
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' }
                  ]}
                />
              </div>
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => {
                    const prevPage = Math.max(1, currentPage - 1);
                    setCurrentPage(prevPage);
                    loadBoxes({ page: prevPage });
                  }}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <span className="pagination-info">
                  Página {currentPage} de {totalPages || 1}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => {
                    const nextPage = Math.min(totalPages, currentPage + 1);
                    setCurrentPage(nextPage);
                    loadBoxes({ page: nextPage });
                  }}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Editar Caixa/Mala' : 'Nova Caixa/Mala'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>
                <HiX size={24} />
              </button>
            </div>

            {/* Wizard Progress Indicator */}
            <div ref={wizardProgressRef} className="wizard-progress">
              <div className="wizard-steps">
                {wizardStepsDisplay.map(({ step, label }, idx) => (
                  <div
                    key={step}
                    className={`wizard-step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''} ${editingId ? 'clickable' : ''}`}
                    onClick={() => {
                      if (editingId) {
                        setCurrentStep(step);
                      }
                    }}
                    style={{
                      cursor: editingId ? 'pointer' : 'default'
                    }}
                    role={editingId ? 'button' : undefined}
                    tabIndex={editingId ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (editingId && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        setCurrentStep(step);
                      }
                    }}
                  >
                    <div className="wizard-step-circle">
                      {currentStep > step ? '✓' : canManageBoxTarifas ? step : idx + 1}
                    </div>
                    <div className="wizard-step-label">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            <form ref={modalFormRef} onSubmit={handleSubmit} className="modal-form">
              <div className="wizard-content">
                {/* Etapa 1: Informações Básicas */}
                <div className={`wizard-step-content ${currentStep === 1 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Informações Básicas</h3>
                  {!editingId && (
                    <div className="form-row">
                      <div className="form-group full-width">
                        <label>Coleta (opcional)</label>
                        <Select
                          value={selectedColetaId}
                          onChange={(e) => handleColetaPrefillChange(e.target.value)}
                          options={[
                            {
                              value: '',
                              label: 'Nenhuma — preencher manualmente',
                            },
                            ...coletasForModal.map((c) => ({
                              value: String(c.id),
                              label: `${c.codigo || c.codigo_pacote || '—'} — ${c.cliente_nome || 'Cliente'}`,
                            })),
                          ]}
                        />
                        <small className="input-hint">
                          Ao escolher uma coleta, cliente, peso, tarifa, origem, destino e destinatário são
                          preenchidos quando disponíveis; você pode ajustar o restante nas próximas etapas.
                        </small>
                      </div>
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Código</label>
                      <input
                        type="text"
                        value={formData.codigo}
                        readOnly
                        className="readonly-input"
                        placeholder="Gerado automaticamente"
                      />
                      <small className="input-hint">Código gerado automaticamente</small>
                    </div>
                    <div className="form-group">
                      <label>Tipo</label>
                      <Select
                        value={formData.tipo}
                        onChange={(e) => {
                          const newTipo = e.target.value;
                          void (async () => {
                            try {
                              let codes = packageCodesRef.current;
                              if (!codes || codes.length === 0) {
                                codes = await loadAllCodesForSequence();
                                packageCodesRef.current = codes;
                              }
                              const newCode = nextPackageCodeForTipo(codes, newTipo);
                              setFormData((prev) => ({
                                ...prev,
                                tipo: newTipo,
                                codigo: newCode,
                              }));
                            } catch (err) {
                              showWarning(
                                err?.message ||
                                  'Não foi possível recalcular o código para o tipo selecionado.'
                              );
                            }
                          })();
                        }}
                        required
                        options={[
                          { value: 'caixa', label: 'Caixa' },
                          { value: 'mala', label: 'Mala' },
                          { value: 'pallet', label: 'Pallet' },
                          { value: 'container', label: 'Container' }
                        ]}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Cliente {labelReq}
                      </label>
                      <ClientSearchSelect
                        id="field-cliente_id"
                        value={formData.cliente_id}
                        selectedNome={formData.cliente_nome}
                        clients={clients}
                        onRefreshClients={loadClients}
                        onNotify={({ type, message }) => {
                          if (type === 'success') showSuccess(message);
                          else showWarning(message);
                        }}
                        onChange={({ cliente_id, cliente_nome }) => {
                          setFormData({
                            ...formData,
                            cliente_id,
                            cliente_nome,
                          });
                          if (invalidFields['cliente_id']) {
                            setInvalidFields(prev => {
                              const newFields = { ...prev };
                              delete newFields['cliente_id'];
                              return newFields;
                            });
                          }
                        }}
                        placeholder="Selecione ou busque um cliente"
                        invalid={!!invalidFields['cliente_id']}
                        disabled={saving}
                      />
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <Select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        required
                        options={STATUS_LIST.map(s => ({
                          value: s.value,
                          label: s.label
                        }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Etapa 2: Dimensões e Peso */}
                <div className={`wizard-step-content ${currentStep === 2 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Dimensões e Peso</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Peso bruto (kg) {labelReq}
                      </label>
                      <input
                        id="field-peso"
                        type="text"
                        value={formData.peso}
                        onChange={(e) => {
                          handlePesoChange(e.target.value);
                          // Limpar validação ao digitar
                          if (invalidFields['peso']) {
                            setInvalidFields(prev => {
                              const newFields = { ...prev };
                              delete newFields['peso'];
                              return newFields;
                            });
                          }
                        }}
                        placeholder="15"
                        className={invalidFields['peso'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Comprimento (cm) {labelReq}
                      </label>
                      <input
                        id="field-dim-comprimento"
                        type="text"
                        value={formData.dimensoes.comprimento}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            dimensoes: { ...formData.dimensoes, comprimento: e.target.value }
                          });
                          if (invalidFields['dim_comprimento']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.dim_comprimento;
                              return n;
                            });
                          }
                        }}
                        placeholder="50"
                        className={invalidFields['dim_comprimento'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Largura (cm) {labelReq}
                      </label>
                      <input
                        id="field-dim-largura"
                        type="text"
                        value={formData.dimensoes.largura}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            dimensoes: { ...formData.dimensoes, largura: e.target.value }
                          });
                          if (invalidFields['dim_largura']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.dim_largura;
                              return n;
                            });
                          }
                        }}
                        placeholder="40"
                        className={invalidFields['dim_largura'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Altura (cm) {labelReq}
                      </label>
                      <input
                        id="field-dim-altura"
                        type="text"
                        value={formData.dimensoes.altura}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            dimensoes: { ...formData.dimensoes, altura: e.target.value }
                          });
                          if (invalidFields['dim_altura']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.dim_altura;
                              return n;
                            });
                          }
                        }}
                        placeholder="30"
                        className={invalidFields['dim_altura'] ? 'field-invalid' : ''}
                      />
                    </div>
                  </div>
                  {!canManageBoxTarifas && (
                    <p className="input-hint" style={{ marginTop: '0.75rem' }}>
                      Ao avançar, o valor da remessa será calculado com a tarifa padrão (base {DEFAULT_GERENTE_TARIFA_ID}).
                    </p>
                  )}
                </div>

                {canManageBoxTarifas && (
                <div className={`wizard-step-content ${currentStep === 3 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Tarifas e Valores</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Tarifa</label>
                      <Select
                        value={formData.tarifa_id}
                        onChange={(e) => handleTarifaChange(e.target.value)}
                        options={[
                          { value: '', label: 'Selecione uma tarifa' },
                          ...tarifas
                            .filter((tarifa) => {
                              const ativa =
                                tarifa?.ativo !== false &&
                                tarifa?.ativo !== 0 &&
                                tarifa?.ativo !== '0';
                              if (ativa) return true;
                              return (
                                formData.tarifa_id != null &&
                                formData.tarifa_id !== '' &&
                                String(tarifa.id) === String(formData.tarifa_id)
                              );
                            })
                            .map(tarifa => {
                            // Formatar valor da tarifa
                            const valorFormatado = tarifa.valor
                              ? typeof tarifa.valor === 'number'
                                ? tarifa.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                : parseFloat(tarifa.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                              : '0,00';
                            const inativa =
                              tarifa?.ativo === false ||
                              tarifa?.ativo === 0 ||
                              tarifa?.ativo === '0';
                            return {
                              value: tarifa.id,
                              label: `${tarifa.nome} - ${tarifa.origem} → ${tarifa.destino} (R$ ${valorFormatado})${inativa ? ' (inativa)' : ''}`
                            };
                          })
                        ]}
                      />
                    </div>
                    <div className="form-group">
                      <label>Taxa Intermediação</label>
                      <input
                        type="text"
                        value={formData.tarifa_intermediacao || ''}
                        onChange={(e) => handleTarifaIntermediacaoChange(e.target.value)}
                        placeholder="R$ 0,00"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Taxa Coleta</label>
                      <input
                        type="text"
                        value={formData.tarifa_coleta || ''}
                        onChange={(e) => handleTarifaColetaChange(e.target.value)}
                        placeholder="R$ 0,00"
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Valor da Remessa {labelReq}
                      </label>
                      <input
                        id="field-valor_remessa"
                        type="text"
                        value={formData.valor_remessa}
                        onChange={(e) => {
                          const formatted = formatCurrency(e.target.value);
                          setFormData({ ...formData, valor_remessa: formatted });
                          if (invalidFields['valor_remessa']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.valor_remessa;
                              return n;
                            });
                          }
                        }}
                        placeholder="R$ 0,00"
                        className={invalidFields['valor_remessa'] ? 'field-invalid' : ''}
                      />
                    </div>
                  </div>
                </div>
                )}

                {/* Etapa 4: Conteúdo e Observações */}
                <div className={`wizard-step-content ${currentStep === 4 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Conteúdo e Observações</h3>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>
                        Conteúdo {labelReq}
                      </label>
                      <input
                        id="field-conteudo"
                        type="text"
                        value={formData.conteudo}
                        onChange={(e) => {
                          setFormData({ ...formData, conteudo: e.target.value });
                          if (invalidFields['conteudo']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.conteudo;
                              return n;
                            });
                          }
                        }}
                        placeholder="Descrição do conteúdo"
                        className={invalidFields['conteudo'] ? 'field-invalid' : ''}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Observação</label>
                      <textarea
                        value={formData.observacao}
                        onChange={(e) => setFormData({ ...formData, observacao: e.target.value })}
                        placeholder="Observações adicionais"
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.5rem',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Etapa 5: Origem e Destino */}
                <div className={`wizard-step-content ${currentStep === 5 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Origem e Destino</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        Origem {labelReq}
                      </label>
                      <input
                        id="field-origem"
                        type="text"
                        value={formData.origem || ''}
                        onChange={(e) => {
                          setFormData({ ...formData, origem: e.target.value });
                          if (invalidFields['origem']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.origem;
                              return n;
                            });
                          }
                        }}
                        placeholder="Cidade/País de origem"
                        disabled={canManageBoxTarifas && !!formData.tarifa_id}
                        className={invalidFields['origem'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>
                        Destino {labelReq}
                      </label>
                      <input
                        id="field-destino"
                        type="text"
                        value={formData.destino || ''}
                        onChange={(e) => {
                          setFormData({ ...formData, destino: e.target.value });
                          if (invalidFields['destino']) {
                            setInvalidFields((prev) => {
                              const n = { ...prev };
                              delete n.destino;
                              return n;
                            });
                          }
                        }}
                        placeholder="Cidade/País de destino"
                        disabled={canManageBoxTarifas && !!formData.tarifa_id}
                        className={invalidFields['destino'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>País de Trânsito</label>
                      <input
                        type="text"
                        value={formData.pais_transito}
                        onChange={(e) => setFormData({ ...formData, pais_transito: e.target.value })}
                        placeholder="País de trânsito (opcional)"
                      />
                    </div>
                  </div>
                </div>

                {/* Etapa 6: Destinatário */}
                <div className={`wizard-step-content ${currentStep === 6 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Destinatário</h3>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Nome Destinatário</label>
                      <input
                        id="field-nome_destinatario"
                        type="text"
                        value={formData.nome_destinatario || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, nome_destinatario: e.target.value })
                        }
                        placeholder="Nome do destinatário"
                      />
                    </div>
                    <div className="form-group">
                      <label>Telefone Destinatário</label>
                      <input
                        id="field-telefone_destinatario"
                        type="text"
                        value={formData.telefone_destinatario || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, telefone_destinatario: e.target.value })
                        }
                        placeholder="Telefone do destinatário"
                      />
                    </div>
                  </div>
                </div>

                {/* Etapa 8: Itens (após NF — pode vir preenchido pela extração) */}
                <div className={`wizard-step-content ${currentStep === 8 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)', fontSize: '1.125rem', fontWeight: '600' }}>Itens da Caixa/Mala</h3>
                  {(!formData.itens || formData.itens.length === 0) && (
                    <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                      Nenhum item foi extraído automaticamente da nota fiscal. Preencha os itens manualmente abaixo.
                    </p>
                  )}
                  {formData.itens && formData.itens.length > 0 && formData.itens.some((it) => it.from_nf) && (
                    <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                      Itens preenchidos a partir da nota fiscal. Você pode editar, remover ou adicionar novos.
                    </p>
                  )}

                {/* Formulário para adicionar novo item */}
                <div className="item-form">
                  <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                    <div className="form-group">
                      <label>Nome do Item *</label>
                      <input
                        id="field-item-nome"
                        type="text"
                        value={currentItem.nome || ''}
                        onChange={handleItemNameChange}
                        placeholder="Nome do item"
                        className={invalidFields['item-nome'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group" style={{ position: 'relative' }}>
                      <label>NCM</label>
                      <input
                        type="text"
                        value={currentItem.ncm || ''}
                        onChange={(e) => {
                          setCurrentItem({ ...currentItem, ncm: e.target.value });
                          setShowNcmDropdown(false);
                        }}
                        onFocus={() => {
                          if (ncmSuggestions.length > 0) {
                            setShowNcmDropdown(true);
                          }
                        }}
                        placeholder="1234.56.78"
                        style={{ position: 'relative' }}
                      />
                      {showNcmDropdown && ncmSuggestions.length > 0 && (
                        <div
                          className="ncm-dropdown"
                          style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            zIndex: 1000,
                            maxHeight: '200px',
                            overflowY: 'auto',
                            marginTop: '0.25rem'
                          }}
                        >
                          {ncmSuggestions.map((ncm, index) => (
                            <div
                              key={index}
                              onClick={() => {
                                setCurrentItem({ ...currentItem, ncm });
                                setShowNcmDropdown(false);
                              }}
                              style={{
                                padding: '0.75rem 1rem',
                                cursor: 'pointer',
                                borderBottom: index < ncmSuggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                                transition: 'background-color 0.2s',
                                fontSize: '0.875rem'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f9fafb';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'white';
                              }}
                            >
                              {ncm}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label>CFOP</label>
                      <input
                        type="text"
                        value={currentItem.cfop || ''}
                        onChange={(e) => setCurrentItem({ ...currentItem, cfop: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                        placeholder="5102"
                        maxLength={4}
                      />
                    </div>
                    <div className="form-group">
                      <label>Descrição do Produto</label>
                      <input
                        type="text"
                        value={currentItem.descricao || ''}
                        onChange={(e) => setCurrentItem({ ...currentItem, descricao: e.target.value })}
                        placeholder="Descrição detalhada do produto"
                      />
                    </div>
                    <div className="form-group">
                      <label>Peso do item (kg) *</label>
                      <input
                        id="field-item-peso"
                        type="text"
                        value={currentItem.peso || ''}
                        onChange={(e) => {
                          const formatted = formatWeight(e.target.value);
                          setCurrentItem({ ...currentItem, peso: formatted });
                          // Limpar validação ao digitar
                          if (invalidFields['item-peso']) {
                            setInvalidFields(prev => {
                              const newFields = { ...prev };
                              delete newFields['item-peso'];
                              return newFields;
                            });
                          }
                        }}
                        placeholder="0.5"
                        className={invalidFields['item-peso'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>Preço Unitário do Item (R$) *</label>
                      <input
                        id="field-item-preco"
                        type="text"
                        value={currentItem.preco || ''}
                        onChange={(e) => {
                          const formatted = formatCurrency(e.target.value);
                          setCurrentItem({ ...currentItem, preco: formatted });
                          // Limpar validação ao digitar
                          if (invalidFields['item-preco']) {
                            setInvalidFields(prev => {
                              const newFields = { ...prev };
                              delete newFields['item-preco'];
                              return newFields;
                            });
                          }
                        }}
                        placeholder="R$ 0,00"
                        className={invalidFields['item-preco'] ? 'field-invalid' : ''}
                      />
                    </div>
                    <div className="form-group">
                      <label>Quantidade</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={currentItem.quantidade || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.,]/g, '');
                          setCurrentItem({ ...currentItem, quantidade: value === '' ? '' : value });
                        }}
                        placeholder="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Unidade</label>
                      <input
                        type="text"
                        value={currentItem.unidade || ''}
                        onChange={(e) => setCurrentItem({ ...currentItem, unidade: e.target.value.toUpperCase().slice(0, 6) })}
                        placeholder="UN"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group" style={{ width: '100%', gridColumn: '1 / -1' }}>
                      <label>Fotos do Item</label>
                      <div className="photo-options" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'nowrap', marginBottom: '12px', width: '100%' }}>
                        <button
                          type="button"
                          className="btn-camera"
                          onClick={() => handleOpenCamera(true, false, false)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 20px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <HiCamera size={20} />
                          Tirar Foto
                        </button>
                        <span style={{ color: '#666', whiteSpace: 'nowrap' }}>ou</span>
                        <label
                          className="btn-select-file"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 20px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <HiCloudUpload size={20} />
                          Galeria
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={async (e) => {
                              if (e.target.files && e.target.files.length > 0) {
                                const files = Array.from(e.target.files);
                                const compressedFiles = await Promise.all(
                                  files.map(async (file) => {
                                    const compressed = await compressImage(file);
                                    return {
                                      id: createLocalId('arquivo'),
                                      file: compressed,
                                      preview: URL.createObjectURL(compressed),
                                      name: compressed.name
                                    };
                                  })
                                );
                                setCurrentItem({ ...currentItem, fotos: [...(currentItem.fotos || []), ...compressedFiles] });
                              }
                            }}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                      {currentItem.fotos && currentItem.fotos.length > 0 && (
                        <div className="photo-preview-list">
                          {currentItem.fotos.map((photo, idx) => {
                            // Verificar se preview é válido (não é blob URL inválido)
                            const previewUrl = photo.preview && typeof photo.preview === 'string' && photo.preview.startsWith('blob:')
                              ? photo.preview
                              : (photo.file ? URL.createObjectURL(photo.file) : null);

                            return (
                              <div key={photo.id || `current-item-photo-${idx}`} className="photo-preview-item">
                                {previewUrl ? (
                                  <img
                                    src={previewUrl}
                                    alt={photo.name || 'Foto'}
                                    onError={(e) => {
                                      // Se der erro, tentar criar novo blob URL
                                      if (photo.file && photo.file instanceof File) {
                                        try {
                                          e.target.src = URL.createObjectURL(photo.file);
                                        } catch (err) {
                                          e.target.style.display = 'none';
                                        }
                                      } else {
                                        e.target.style.display = 'none';
                                      }
                                    }}
                                  />
                                ) : (
                                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Sem preview</div>
                                )}
                                <button
                                  type="button"
                                  className="btn-icon btn-delete-small"
                                  onClick={() => {
                                    // Revogar blob URL antes de remover
                                    if (previewUrl && previewUrl.startsWith('blob:')) {
                                      try {
                                        URL.revokeObjectURL(previewUrl);
                                      } catch (e) {
                                        // Ignorar erros ao revogar
                                      }
                                    }
                                    const updatedFotos = currentItem.fotos.filter((_, i) => i !== idx);
                                    setCurrentItem({ ...currentItem, fotos: updatedFotos });
                                  }}
                                  style={{ position: 'absolute', top: '4px', right: '4px', backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                >
                                  <HiX size={14} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-row" style={{ justifyContent: 'flex-end' }}>
                    <div className="form-group">
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleAddItem}>
                        <HiPlus size={16} />
                        Confirmar Item
                      </button>
                    </div>
                  </div>
                </div>

                {/* Lista de itens adicionados */}
                {formData.itens && formData.itens.length > 0 && (
                  <div className="items-list">
                    {formData.itens.map((item, itemIndex) => (
                      <div key={item.id || `item-${itemIndex}`} className="item-card">
                        <div className="item-header">
                          <div className="item-info">
                            <strong>{item.nome}</strong>
                            {(item.ncm || item.cfop || item.descricao) && (
                              <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                {item.ncm && <span>NCM: {item.ncm}</span>}
                                {item.ncm && item.cfop && <span> | </span>}
                                {item.cfop && <span>CFOP: {item.cfop}</span>}
                                {(item.ncm || item.cfop) && item.descricao && <span> | </span>}
                                {item.descricao && <span>{item.descricao}</span>}
                              </div>
                            )}
                            <span>Qtd: {item.quantidade}{item.unidade ? ` ${item.unidade}` : ''} | Peso: {item.peso}kg | Preço: {displayCurrency(item.preco)}</span>
                          </div>
                        </div>
                        {item.fotos && item.fotos.length > 0 && (
                          <div className="item-photos">
                            <label>Fotos do Item:</label>
                            <div className="photo-preview-list">
                              {item.fotos.map((photo, photoIdx) => {
                                // Tratar diferentes formatos de foto: {url}, {preview}, string direto, ou File
                                const rawPhotoUrl = photo?.url || photo?.preview || (typeof photo === 'string' ? photo : null);
                                // Normalizar URL da imagem (remover barras invertidas e adicionar public/ se necessário)
                                const photoUrl = rawPhotoUrl ? normalizeImageUrl(rawPhotoUrl) : null;
                                const photoId = photo?.id || `item-${itemIndex}-photo-${photoIdx}`;

                                return (
                                  <div key={photoId} className="photo-preview-item">
                                    {photoUrl ? (
                                      <img
                                        src={photoUrl}
                                        alt={`Foto ${photoIdx + 1} do item ${item.nome || item.name || ''}`}
                                        onError={(e) => {
                                          // Tentar recriar blob URL se for um File
                                          if (photo.file && photo.file instanceof File) {
                                            try {
                                              const newUrl = URL.createObjectURL(photo.file);
                                              e.target.src = newUrl;
                                              return;
                                            } catch (err) {
                                              // Se falhar, ocultar a imagem
                                            }
                                          }
                                          // Se não conseguir recriar, ocultar a imagem
                                          e.target.style.display = 'none';
                                          // Adicionar mensagem de erro de forma segura (sem innerHTML)
                                          const errorDiv = document.createElement('div');
                                          errorDiv.style.cssText = 'display: flex; align-items: center; justify-content: center; height: 100%; color: #999; font-size: 0.75rem;';
                                          errorDiv.textContent = 'Erro ao carregar';
                                          if (e.target.parentElement) {
                                            e.target.parentElement.appendChild(errorDiv);
                                          }
                                        }}
                                      />
                                    ) : (
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999', fontSize: '0.75rem' }}>
                                        Sem preview
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                </div>

                {/* Etapa 7: Nota Fiscal (antes dos itens para autofill) */}
                <div className={`wizard-step-content ${currentStep === 7 ? 'active' : ''}`}>
                  <h3 style={{ marginBottom: '1.5rem', color: 'var(--text-primary)', fontSize: '1.25rem' }}>Nota Fiscal</h3>
                <div className="form-group">
                  <label>Upload de Nota Fiscal</label>
                  <p style={{ margin: '0 0 0.75rem', color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                    Anexe PDF ou imagem da NF (pode ser mais de uma). Os itens identificados serão preenchidos automaticamente na próxima etapa; se nada for encontrado, preencha manualmente.
                  </p>
                  {extractingNf && (
                    <p style={{ margin: '0 0 0.75rem', color: '#2563eb', fontSize: '0.8125rem', fontWeight: 600 }}>
                      Extraindo informações da nota fiscal…
                    </p>
                  )}
                  <div className="photo-options" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'nowrap', marginBottom: '12px' }}>
                    <button
                      type="button"
                      className="btn-camera"
                      onClick={() => handleOpenCamera(false, true, false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <HiCamera size={20} />
                      Tirar Foto
                    </button>
                    <span style={{ color: '#666', whiteSpace: 'nowrap' }}>ou</span>
                    <label
                      htmlFor="nota-fiscal-upload"
                      className="file-upload-label"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 20px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <HiCloudUpload size={20} />
                      Galeria
                    </label>
                    <input
                      type="file"
                      id="nota-fiscal-upload"
                      multiple
                      accept="image/*,.pdf,application/pdf,.xml,text/xml,application/xml"
                      onChange={async (e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          const files = Array.from(e.target.files);
                          const processedFiles = await Promise.all(
                            files.map(async (file) => {
                              // Se for imagem, comprimir; se for PDF, manter como está
                              if (file.type.startsWith('image/')) {
                                const compressed = await compressImage(file);
                                return {
                                  id: createLocalId('arquivo'),
                                  file: compressed,
                                  preview: URL.createObjectURL(compressed),
                                  name: compressed.name
                                };
                              } else {
                                return {
                                  id: createLocalId('arquivo'),
                                  file,
                                  preview: null,
                                  name: file.name
                                };
                              }
                            })
                          );
                          handleNotaFiscalUpload(processedFiles);
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                  </div>
                  {formData.nota_fiscal && (
                    <div className="nota-fiscal-list">
                      {(Array.isArray(formData.nota_fiscal)
                        ? formData.nota_fiscal
                        : [formData.nota_fiscal]
                      ).map((file, fileIdx) => {
                        const nome = file?.name || file?.nome_arquivo || `Nota Fiscal ${fileIdx + 1}`;
                        const preview = file?.preview;
                        const ehImagem =
                          typeof preview === 'string' &&
                          (preview.startsWith('blob:') ||
                            preview.startsWith('data:image/') ||
                            /\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i.test(preview));

                        return (
                          <div key={file?.id || `nota-fiscal-${fileIdx}`} className="nota-fiscal-item">
                            <div className="file-info">
                              <HiDocument size={20} />
                              {file?.url ? (
                                <a href={file.url} target="_blank" rel="noopener noreferrer">
                                  {nome}
                                </a>
                              ) : (
                                <span>{nome}</span>
                              )}
                            </div>
                            {ehImagem && (
                              <img src={preview} alt={nome} className="nota-fiscal-preview" />
                            )}
                            <button
                              type="button"
                              className="btn-icon btn-delete"
                              onClick={() => handleRemoveNotaFiscal(fileIdx)}
                              title="Remover"
                            >
                              <HiTrash size={18} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Wizard Navigation */}
              <div className="wizard-actions">
                <div className="wizard-actions-left">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                    disabled={saving}
                    style={{
                      minWidth: '120px',
                      padding: '0.75rem 1.5rem',
                      color: '#ffffff',
                      backgroundColor: '#6b7280',
                      borderColor: '#6b7280'
                    }}
                  >
                    Cancelar
                  </button>
                </div>
                <div className="wizard-actions-right">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handlePrevStep}
                      disabled={saving}
                      style={{
                        minWidth: '120px',
                        padding: '0.75rem 1.5rem',
                        color: '#ffffff',
                        backgroundColor: '#6b7280',
                        borderColor: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                    >
                      <HiChevronLeft size={18} style={{ color: '#ffffff' }} />
                      <span style={{ color: '#ffffff' }}>Anterior</span>
                    </button>
                  )}
                  {currentStep < totalSteps && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleNextStep();
                      }}
                      disabled={saving || currentStep >= totalSteps}
                      style={{
                        minWidth: '120px',
                        padding: '0.75rem 1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                    >
                      <span>Próximo</span>
                      <HiChevronRight size={18} />
                    </button>
                  )}
                  {(editingId || currentStep === totalSteps) && (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                      style={{
                        minWidth: '120px',
                        padding: '0.75rem 1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      {saving ? (
                        <>
                          <HiRefresh size={18} style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} />
                          <span>{editingId ? 'Atualizando...' : 'Salvando...'}</span>
                        </>
                      ) : (
                        <span>{editingId ? 'Atualizar' : 'Salvar'} Caixa/Mala</span>
                      )}
                    </button>
                  )}
                </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Nota Fiscal */}
      {showNotaFiscalModal && selectedNotaFiscal && (
        <div className="modal-overlay" onClick={() => setShowNotaFiscalModal(false)}>
          <div className="modal-content" style={{ maxWidth: '90vw', maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nota Fiscal</h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowNotaFiscalModal(false);
                  setSelectedNotaFiscal(null);
                }}
                title="Fechar"
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1rem', overflow: 'auto' }}>
              {(() => {
                // O modal só deve abrir para imagens, então aqui só renderizamos imagens
                // Verificar se é PDF primeiro (para garantir que não é)
                const isPDF = typeof selectedNotaFiscal === 'string' && (
                  selectedNotaFiscal.match(/\.(pdf)$/i) ||
                  selectedNotaFiscal.toLowerCase().includes('application/pdf') ||
                  selectedNotaFiscal.toLowerCase().includes('pdf')
                );

                // Verificar se é imagem (e não é PDF)
                const isImage = typeof selectedNotaFiscal === 'string' &&
                  !isPDF && (
                    selectedNotaFiscal.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i) ||
                    selectedNotaFiscal.match(/^data:image\//) ||
                    selectedNotaFiscal.toLowerCase().includes('image/') ||
                    selectedNotaFiscal.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)\?/i) // Com query params
                  );

                if (isImage) {
                  return (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '1rem'
                    }}>
                      <img
                        src={selectedNotaFiscal}
                        alt="Nota Fiscal"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '70vh',
                          height: 'auto',
                          display: 'block',
                          margin: '0 auto',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 2rem; text-align: center;">
                              <p style="margin-bottom: 1rem;">Não foi possível carregar a imagem.</p>
                              <a href="${selectedNotaFiscal}" target="_blank" style="color: #3b82f6; text-decoration: underline;">
                                Clique aqui para abrir em nova aba
                              </a>
                            </div>
                          `;
                        }}
                      />
                      <a
                        href={selectedNotaFiscal}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          textDecoration: 'none',
                          marginTop: '0.5rem'
                        }}
                      >
                        <HiExternalLink size={16} />
                        Abrir em Nova Aba
                      </a>
                    </div>
                  );
                } else {
                  // Fallback: tentar exibir como PDF ou oferecer download
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center', minHeight: '400px' }}>
                      <p style={{ marginBottom: '1rem', color: '#666' }}>
                        Visualização não disponível para este tipo de arquivo.
                      </p>
                      <a
                        href={selectedNotaFiscal}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-block',
                          padding: '0.75rem 1.5rem',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          textDecoration: 'none',
                          borderRadius: '0.375rem',
                          fontWeight: '600'
                        }}
                      >
                        Abrir arquivo em nova aba
                      </a>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Códigos de Acesso */}
      {showCodesModal && selectedBoxCodes && (
        <div className="modal-overlay" onClick={() => setShowCodesModal(false)}>
          <div className="modal-content codes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Códigos de Acesso - {selectedBoxCodes.codigo}</h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowCodesModal(false);
                  setSelectedBoxCodes(null);
                }}
                title="Fechar"
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="codes-list">
                <div className="code-row">
                  <div className="code-label">
                    <strong>Cliente</strong>
                  </div>
                  <div className="code-content">
                    <span className="code-text">{selectedBoxCodes.codigos_acesso?.cliente || 'Não gerado'}</span>
                    {selectedBoxCodes.codigos_acesso?.cliente ? (
                      <button
                        className="btn-copy-code"
                        onClick={() => {
                          navigator.clipboard.writeText(selectedBoxCodes.codigos_acesso.cliente);
                          showSuccess('Código copiado!');
                        }}
                        title="Copiar código"
                      >
                        <HiClipboardCopy size={18} />
                      </button>
                    ) : (
                      <button
                        className="btn-copy-code"
                        type="button"
                        onClick={async () => {
                          try {
                            const response = await apiService.getBoxCodes(selectedBoxCodes.id);
                            const codigos = response?.data || response || {};
                            const cliente = codigos.cliente || '';
                            setSelectedBoxCodes((prev) => ({
                              ...prev,
                              codigos_acesso: { cliente },
                            }));
                            if (cliente) {
                              showSuccess('Código do cliente gerado!');
                              loadBoxes();
                            } else {
                              showWarning('Não foi possível gerar o código');
                            }
                          } catch (error) {
                            showWarning(error?.message || 'Erro ao gerar código');
                          }
                        }}
                        title="Gerar código"
                      >
                        <HiRefresh size={18} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowCodesModal(false);
                    setSelectedBoxCodes(null);
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Itens da Caixa */}
      {showItemsModal && selectedBoxItems && (
        <div className="modal-overlay" onClick={() => setShowItemsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Itens da Caixa - {selectedBoxItems.codigo || selectedBoxItems.codigo_pacote}</h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowItemsModal(false);
                  setSelectedBoxItems(null);
                }}
                title="Fechar"
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body">
              {selectedBoxItems.itens && selectedBoxItems.itens.length > 0 ? (
                <div className="items-list">
                  {selectedBoxItems.itens.map((item, index) => (
                    <div key={item.id || index} className="item-detail-card">
                      <div className="item-detail-header">
                        <div className="item-detail-info">
                          <h4>{item.nome || item.name || `Item ${index + 1}`}</h4>
                          <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem', marginBottom: '0.5rem' }}>
                            {item.ncm && <span><strong>NCM:</strong> {item.ncm}</span>}
                            {item.ncm && item.cfop && <span> | </span>}
                            {item.cfop && <span><strong>CFOP:</strong> {item.cfop}</span>}
                            {(item.ncm || item.cfop) && item.descricao && <span> | </span>}
                            {item.descricao && <span><strong>Descrição:</strong> {item.descricao}</span>}
                          </div>
                          <div className="item-detail-meta">
                            <span><strong>Quantidade:</strong> {item.quantidade || item.quantity || 'N/A'}{item.unidade ? ` ${item.unidade}` : ''}</span>
                            <span><strong>Peso:</strong> {item.peso || item.weight || 'N/A'} {item.peso || item.weight ? 'kg' : ''}</span>
                            <span><strong>Preço:</strong> {item.preco ? displayCurrency(item.preco) : item.price ? displayCurrency(item.price) : 'N/A'}</span>
                          </div>
                        </div>
                        {canCreateEditBoxes && (
                          <button
                            type="button"
                            className="btn-icon btn-delete"
                            onClick={() => {
                              setItemToDelete(item);
                              setShowDeleteItemDialog(true);
                            }}
                            title="Excluir item"
                          >
                            <HiTrash size={18} />
                          </button>
                        )}
                      </div>
                      {item.fotos && Array.isArray(item.fotos) && item.fotos.length > 0 && (
                        <div className="item-detail-photos">
                          <label>Fotos do Item ({item.fotos.length}):</label>
                          <div className="photo-gallery">
                            {item.fotos.map((photo, photoIdx) => {
                              // Tratar diferentes formatos de foto: {url}, {preview}, string direto
                              const rawPhotoUrl = photo?.url || photo?.preview || (typeof photo === 'string' ? photo : null);
                              const photoId = photo?.id || `item-${index}-photo-${photoIdx}`;

                              if (!rawPhotoUrl) return null;

                              // Normalizar URL da imagem (remover barras invertidas e adicionar public/ se necessário)
                              const photoUrl = normalizeImageUrl(rawPhotoUrl);

                              return (
                                <div key={photoId} className="photo-gallery-item">
                                  <img
                                    src={photoUrl}
                                    alt={`Foto ${photoIdx + 1} do item ${item.nome || item.name || ''}`}
                                    onClick={() => window.open(photoUrl, '_blank')}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999; font-size: 0.75rem; padding: 0.5rem;">Erro ao carregar imagem</div>';
                                    }}
                                  />
                                  <div className="photo-overlay">
                                    <button
                                      type="button"
                                      className="photo-view-btn"
                                      onClick={() => window.open(photoUrl, '_blank')}
                                      title="Abrir em nova aba"
                                    >
                                      <HiEye size={18} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>Nenhum item cadastrado nesta caixa</p>
                </div>
              )}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowItemsModal(false);
                    setSelectedBoxItems(null);
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showConfirmDialog}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir esta caixa/mala?"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowConfirmDialog(false);
          setBoxToDelete(null);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Modal Reporte Pendência (pagamento na retirada) */}
      {showPendenciaModal && selectedBoxPendencia && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowPendenciaModal(false);
            setSelectedBoxPendencia(null);
            setPendenciaPreview(null);
          }}
        >
          <div
            className="modal-content pendencia-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>
                {pendenciaPreview?.readonly
                  ? 'Pendência na retirada'
                  : 'Reporte Pendência — pagar na retirada'}
              </h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowPendenciaModal(false);
                  setSelectedBoxPendencia(null);
                  setPendenciaPreview(null);
                }}
                title="Fechar"
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Caixa / Mala</label>
                <input
                  type="text"
                  readOnly
                  value={`${selectedBoxPendencia.codigo || selectedBoxPendencia.codigo_pacote || ''} — ${selectedBoxPendencia.cliente_nome || selectedBoxPendencia.cliente?.nome || ''}`}
                />
              </div>

              <div className="pendencia-values-row">
                <div className="pendencia-value-card pendencia-value-card--brl">
                  <label
                    className="pendencia-value-eyebrow"
                    htmlFor="pendencia-valor-brl"
                  >
                    Valor em Real
                  </label>
                  <div className="pendencia-brl-field">
                    <span className="pendencia-brl-symbol">R$</span>
                    <input
                      id="pendencia-valor-brl"
                      type="text"
                      inputMode="decimal"
                      disabled={!!pendenciaPreview?.readonly}
                      value={pendenciaForm.valor_brl}
                      onChange={(e) => {
                        setPendenciaForm((prev) => ({
                          ...prev,
                          valor_brl: e.target.value,
                        }));
                        setPendenciaPreview(null);
                      }}
                      placeholder="0,00"
                    />
                    <span className="pendencia-brl-code">BRL</span>
                  </div>
                  <div className="pendencia-value-meta">Origem da conversão</div>
                </div>

                <div className="pendencia-values-divider" aria-hidden>
                  <span className="pendencia-values-divider-icon">
                    <HiSwitchHorizontal size={16} />
                  </span>
                </div>

                <div
                  className={`pendencia-value-card pendencia-value-card--local${
                    pendenciaPreview ? ' is-filled' : ''
                  }`}
                >
                  <span className="pendencia-value-eyebrow">
                    Valor a cobrar do destinatário
                  </span>
                  <div className="pendencia-value-amount">
                    {pendenciaPreview
                      ? formatMoneyWithFlag(
                          pendenciaPreview.amount_converted,
                          pendenciaPreview.currency || pendenciaForm.moeda_local
                        )
                      : '—'}
                  </div>
                  <div className="pendencia-value-meta">
                    {pendenciaPreview
                      ? `${CURRENCY_META[pendenciaPreview.currency || pendenciaForm.moeda_local]?.flag || ''} ${pendenciaPreview.currency || pendenciaForm.moeda_local} · cotação ${pendenciaPreview.date || '—'}${
                          pendenciaPreview.criado_por?.nome
                            ? ` · ${pendenciaPreview.criado_por.nome}`
                            : selectedBoxPendencia.pendencia_pagamento?.criado_por?.nome
                              ? ` · ${selectedBoxPendencia.pendencia_pagamento.criado_por.nome}`
                              : ''
                        }`
                      : `${CURRENCY_META[pendenciaForm.moeda_local]?.flag || ''} Em ${pendenciaForm.moeda_local} após converter`}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Moeda local na retirada *</label>
                <Select
                  value={pendenciaForm.moeda_local}
                  disabled={!!pendenciaPreview?.readonly}
                  onChange={(e) => {
                    const moeda = e.target.value;
                    setPendenciaForm((prev) => ({ ...prev, moeda_local: moeda }));
                    setPendenciaPreview(null);
                  }}
                  options={LOCAL_CURRENCY_OPTIONS}
                />
              </div>
              <div className="form-group">
                <label>Observação</label>
                <textarea
                  rows={2}
                  disabled={!!pendenciaPreview?.readonly}
                  value={pendenciaForm.observacao}
                  onChange={(e) =>
                    setPendenciaForm((prev) => ({ ...prev, observacao: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="modal-footer pendencia-modal-footer">
              <button
                type="button"
                className="pendencia-btn pendencia-btn--ghost"
                onClick={() => {
                  setShowPendenciaModal(false);
                  setSelectedBoxPendencia(null);
                  setPendenciaPreview(null);
                }}
              >
                Fechar
              </button>
              <div className="pendencia-modal-footer-actions">
                {!pendenciaPreview?.readonly && (
                  <>
                    <button
                      type="button"
                      className="pendencia-btn pendencia-btn--outline"
                      disabled={convertingPendencia}
                      onClick={() =>
                        refreshPendenciaPreview(
                          pendenciaForm.valor_brl,
                          pendenciaForm.moeda_local
                        )
                      }
                    >
                      <HiSwitchHorizontal
                        size={17}
                        className={convertingPendencia ? 'is-spinning' : ''}
                      />
                      {convertingPendencia ? 'Convertendo…' : 'Converter valor local'}
                    </button>
                    <button
                      type="button"
                      className="pendencia-btn pendencia-btn--primary"
                      disabled={savingPendencia || !pendenciaPreview}
                      onClick={handleSavePendencia}
                      title={
                        !pendenciaPreview
                          ? 'Converta o valor local antes de registrar'
                          : undefined
                      }
                    >
                      <HiCheckCircle size={17} />
                      {savingPendencia ? 'Salvando…' : 'Registrar pendência'}
                    </button>
                  </>
                )}
                {pendenciaPreview?.readonly &&
                  selectedBoxPendencia.pendencia_pagamento?.status === 'pendente' &&
                  isBoxesAdmin && (
                    <button
                      type="button"
                      className="pendencia-btn pendencia-btn--danger"
                      onClick={async () => {
                        try {
                          await apiService.cancelPendenciaPagamento(
                            selectedBoxPendencia.pendencia_pagamento.id
                          );
                          showSuccess('Pendência cancelada');
                          setShowPendenciaModal(false);
                          setSelectedBoxPendencia(null);
                          loadBoxes();
                        } catch (error) {
                          showWarning(error?.message || 'Erro ao cancelar');
                        }
                      }}
                    >
                      Cancelar pendência
                    </button>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Registro de Pagamento */}
      {showPaymentModal && selectedBoxForPayment && (
        <div
          className="modal-overlay"
          onClick={() => setShowPaymentModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Registrar Pagamento</h2>
              <button
                className="modal-close"
                onClick={() => setShowPaymentModal(false)}
              >
                <HiX size={24} />
              </button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label>Caixa/Mala</label>
                <input
                  type="text"
                  value={`${selectedBoxForPayment.codigo || selectedBoxForPayment.codigo_pacote || ''} - ${selectedBoxForPayment.cliente_nome || selectedBoxForPayment.cliente?.nome || ''}`}
                  disabled
                  style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>
              {(() => {
                const { total, pago, restante } = getBoxPaymentBalances(selectedBoxForPayment);
                const pend = selectedBoxForPayment.pendencia_pagamento?.status === 'pendente'
                  ? selectedBoxForPayment.pendencia_pagamento
                  : null;
                const pendBrl = pend ? parseMoneyLike(pend.valor_brl) : 0;
                const valorDigitado = parseCurrencyToNumber(boxPayment.valor);
                const novoRestante =
                  restante > 0
                    ? Math.max(0, roundMoney(restante - (valorDigitado > 0 ? valorDigitado : 0)))
                    : 0;
                const pendenciaApos =
                  pend && pendBrl > 0
                    ? Math.min(pendBrl, novoRestante)
                    : null;
                if (!(total > 0 || pago > 0 || pend)) return null;
                return (
                  <div
                    className="form-group"
                    style={{
                      padding: '0.75rem 1rem',
                      background: '#f8fafc',
                      borderRadius: '0.5rem',
                      border: '1px solid #e2e8f0',
                      fontSize: '0.875rem',
                      lineHeight: 1.5,
                    }}
                  >
                    <div>Total {displayCurrency(total)}</div>
                    <div>Pago {displayCurrency(pago)}</div>
                    <div>
                      Restante{' '}
                      <strong style={{ color: restante > 0 ? '#f97316' : '#10b981' }}>
                        {displayCurrency(restante)}
                      </strong>
                    </div>
                    {pend && (
                      <>
                        <div
                          style={{
                            marginTop: '0.5rem',
                            paddingTop: '0.5rem',
                            borderTop: '1px dashed #cbd5e1',
                          }}
                        >
                          Pendência na retirada:{' '}
                          <strong>
                            {formatMoneyWithFlag(pend.valor_local, pend.moeda_local)}
                          </strong>
                          <span style={{ color: '#64748b' }}>
                            {' '}
                            ({displayCurrency(pendBrl)})
                          </span>
                        </div>
                        {valorDigitado > 0 && pendenciaApos !== null && (
                          <div style={{ marginTop: '0.35rem', color: '#475569', fontSize: '0.8125rem' }}>
                            {novoRestante <= 0.009 ? (
                              <>Após este pagamento a pendência na retirada será <strong>encerrada</strong>.</>
                            ) : pendenciaApos + 0.009 < pendBrl ? (
                              <>
                                Após este pagamento a pendência será ajustada para{' '}
                                <strong>{displayCurrency(pendenciaApos)}</strong>
                                {' '}(com nova conversão na moeda local).
                              </>
                            ) : (
                              <>A pendência na retirada permanece {displayCurrency(pendBrl)}.</>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
              <div className="form-group">
                <label>Valor deste pagamento *</label>
                <input
                  type="text"
                  value={boxPayment.valor}
                  onChange={(e) => {
                    const formatted = formatCurrency(e.target.value);
                    setBoxPayment({ ...boxPayment, valor: formatted });
                  }}
                  required
                  placeholder="R$ 0,00"
                />
                <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                  Pode ser parcial. Máximo: {displayCurrency(getBoxPaymentBalances(selectedBoxForPayment).restante || getBoxPaymentBalances(selectedBoxForPayment).total)}
                  {selectedBoxForPayment.pendencia_pagamento?.status === 'pendente'
                    ? ' · Se ultrapassar a diferença livre, a pendência na retirada é recalculada automaticamente.'
                    : ''}
                </small>
              </div>
              <div className="form-group">
                <label>Data do Pagamento</label>
                <input
                  type="date"
                  value={boxPayment.data}
                  onChange={(e) =>
                    setBoxPayment({ ...boxPayment, data: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Método de Pagamento</label>
                <Select
                  value={boxPayment.metodo}
                  onChange={(e) =>
                    setBoxPayment({ ...boxPayment, metodo: e.target.value })
                  }
                  required
                  options={[
                    { value: 'dinheiro', label: 'Dinheiro' },
                    { value: 'pix', label: 'PIX' },
                    { value: 'cartao', label: 'Cartão' },
                    { value: 'transferencia', label: 'Transferência' },
                  ]}
                />
              </div>
              <div className="form-group" style={{ width: '100%', minWidth: '100%' }}>
                <label>Comprovantes *</label>
                <div className="photo-options" style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  marginBottom: '12px',
                  width: '100%',
                  minWidth: '100%',
                  overflow: 'hidden'
                }}>
                  <button
                    type="button"
                    className="btn-camera"
                    onClick={() => handleOpenCamera(false, false, true)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                  >
                    <HiCamera size={20} />
                    Tirar Foto
                  </button>
                  <span style={{ color: '#666', whiteSpace: 'nowrap', flexShrink: 0 }}>ou</span>
                  <label
                    htmlFor="payment-receipt-upload"
                    className="file-upload-label"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                  >
                    <HiCloudUpload size={20} />
                    Galeria / Arquivos
                  </label>
                  <input
                    type="file"
                    id="payment-receipt-upload"
                    accept="image/*,.pdf"
                    multiple
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length === 0) return;
                      const added = [];
                      for (const file of files) {
                        const ready = file.type.startsWith('image/')
                          ? await compressImage(file)
                          : file;
                        added.push({
                          file: ready,
                          name: ready.name || file.name,
                          preview: ready.type?.startsWith('image/')
                            ? URL.createObjectURL(ready)
                            : null,
                        });
                      }
                      setBoxPayment((prev) => ({
                        ...prev,
                        comprovantes: [...(prev.comprovantes || []), ...added],
                      }));
                      e.target.value = '';
                    }}
                    style={{ display: 'none' }}
                  />
                </div>
                {(boxPayment.comprovantes || []).length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {boxPayment.comprovantes.map((entry, idx) => (
                      <div
                        key={`${entry.name || 'file'}-${idx}`}
                        className="file-preview"
                        style={{
                          padding: '0.5rem',
                          background: '#f3f4f6',
                          borderRadius: '0.375rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '0.5rem',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                          {entry.preview ? (
                            <img
                              src={entry.preview}
                              alt={entry.name}
                              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                            />
                          ) : null}
                          <span style={{ fontSize: '0.875rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {entry.name || entry.file?.name || `Arquivo ${idx + 1}`}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            setBoxPayment((prev) => ({
                              ...prev,
                              comprovantes: (prev.comprovantes || []).filter((_, i) => i !== idx),
                            }))
                          }
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0.25rem' }}
                          title="Remover arquivo"
                        >
                          <HiX size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                  Pode anexar vários arquivos · Imagem (JPG, PNG, WEBP) ou PDF
                </small>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedBoxForPayment(null);
                    setBoxPayment(emptyBoxPayment());
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleRegisterPayment}
                  disabled={!(boxPayment.comprovantes || []).length}
                >
                  <HiCheckCircle size={18} />
                  Registrar Pagamento
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização de Pagamento */}
      {showViewPaymentModal && selectedPayment && selectedBoxForPayment && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowViewPaymentModal(false);
            setSelectedPayment(null);
            setSelectedBoxForPayment(null);
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Pagamento Registrado</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowViewPaymentModal(false);
                  setSelectedPayment(null);
                  setSelectedBoxForPayment(null);
                }}
              >
                <HiX size={24} />
              </button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label>Caixa/Mala</label>
                <input
                  type="text"
                  value={`${selectedBoxForPayment.codigo || selectedBoxForPayment.codigo_pacote || ''} - ${selectedBoxForPayment.cliente_nome || selectedBoxForPayment.cliente?.nome || ''}`}
                  disabled
                  style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>
              <div className="form-group">
                <label>Valor total</label>
                <input
                  type="text"
                  value={displayCurrency(
                    selectedPayment.valor_total ??
                    selectedBoxForPayment.valor_declarado ??
                    selectedBoxForPayment.valor_remessa ??
                    selectedPayment.valor
                  )}
                  disabled
                  style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>
              {(selectedPayment.valor_pago != null || selectedPayment.valor_restante != null) && (
                <div
                  className="form-group"
                  style={{
                    padding: '0.75rem 1rem',
                    background: '#f8fafc',
                    borderRadius: '0.5rem',
                    border: '1px solid #e2e8f0',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                  }}
                >
                  <div>Pago {displayCurrency(selectedPayment.valor_pago || 0)}</div>
                  <div>
                    Restante{' '}
                    <strong>
                      {displayCurrency(selectedPayment.valor_restante || 0)}
                    </strong>
                  </div>
                  {selectedPayment.status_pagamento && (
                    <div>Status: {selectedPayment.status_pagamento}</div>
                  )}
                </div>
              )}
              {Array.isArray(selectedPayment.pagamentos) && selectedPayment.pagamentos.length > 0 && (
                <div className="form-group">
                  <label>Histórico de pagamentos</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {selectedPayment.pagamentos.map((pag) => (
                      <div
                        key={pag.id}
                        style={{
                          padding: '0.65rem 0.75rem',
                          background: '#f9fafb',
                          borderRadius: '0.375rem',
                          border: '1px solid #e5e7eb',
                          fontSize: '0.8125rem',
                        }}
                      >
                        <div>
                          <strong>{displayCurrency(pag.valor)}</strong>
                          {pag.status ? ` · ${pag.status}` : ''}
                          {pag.metodo_pagamento ? ` · ${pag.metodo_pagamento}` : ''}
                        </div>
                        <div style={{ color: '#6b7280' }}>
                          {pag.data_pagamento
                            ? new Date(pag.data_pagamento).toLocaleString('pt-BR')
                            : pag.created_at
                              ? new Date(pag.created_at).toLocaleString('pt-BR')
                              : ''}
                          {pag.user_criador?.nome ? ` · ${pag.user_criador.nome}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!(Array.isArray(selectedPayment.pagamentos) && selectedPayment.pagamentos.length > 0) && (
                <div className="form-group">
                  <label>Valor</label>
                  <input
                    type="text"
                    value={selectedPayment.valor ? displayCurrency(selectedPayment.valor) : 'N/A'}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {(selectedPayment.data_pagamento || selectedPayment.created_at) && (
                <div className="form-group">
                  <label>Data do Pagamento</label>
                  <input
                    type="text"
                    value={selectedPayment.data_pagamento
                      ? new Date(selectedPayment.data_pagamento).toLocaleDateString('pt-BR')
                      : selectedPayment.created_at
                      ? new Date(selectedPayment.created_at).toLocaleDateString('pt-BR')
                      : 'N/A'}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {(selectedBoxForPayment.status_pagamento && selectedBoxForPayment.status_pagamento.toLowerCase() === 'cancelado') && (
                <>
                  {selectedBoxForPayment.data_cancelamento && (
                    <div className="form-group">
                      <label>Data do Cancelamento</label>
                      <input
                        type="text"
                        value={new Date(selectedBoxForPayment.data_cancelamento).toLocaleString('pt-BR', {
                          timeZone: 'America/Sao_Paulo',
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                        disabled
                        style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                      />
                    </div>
                  )}
                  {selectedBoxForPayment.usuario_cancelamento && (
                    <div className="form-group">
                      <label>Cancelado por</label>
                      <input
                        type="text"
                        value={selectedBoxForPayment.usuario_cancelamento.nome || 'N/A'}
                        disabled
                        style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                      />
                    </div>
                  )}
                </>
              )}
              {selectedPayment.metodo_pagamento && (
                <div className="form-group">
                  <label>Método de Pagamento</label>
                  <input
                    type="text"
                    value={selectedPayment.metodo_pagamento}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {selectedPayment.moeda && (
                <div className="form-group">
                  <label>Moeda</label>
                  <input
                    type="text"
                    value={selectedPayment.moeda}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {selectedPayment.tipo && (
                <div className="form-group">
                  <label>Tipo</label>
                  <input
                    type="text"
                    value={selectedPayment.tipo}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {selectedPayment.descricao && (
                <div className="form-group">
                  <label>Descrição</label>
                  <input
                    type="text"
                    value={selectedPayment.descricao}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {((Array.isArray(selectedPayment.comprovantes_lista) && selectedPayment.comprovantes_lista.length > 0)
                || selectedPayment.comprovante_url) && (
                <div className="form-group">
                  <label>Comprovantes</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    {(Array.isArray(selectedPayment.comprovantes_lista) && selectedPayment.comprovantes_lista.length > 0
                      ? selectedPayment.comprovantes_lista
                      : [{ url: selectedPayment.comprovante_url, nome: 'Comprovante' }]
                    ).map((item, idx) => {
                      let comprovanteUrl = item.url || item;
                      if (comprovanteUrl && !String(comprovanteUrl).startsWith('http://') && !String(comprovanteUrl).startsWith('https://')) {
                        const baseUrl = 'https://apicargo.sendingto.com';
                        comprovanteUrl = String(comprovanteUrl).startsWith('/')
                          ? `${baseUrl}${comprovanteUrl}`
                          : `${baseUrl}/${comprovanteUrl}`;
                      }
                      return (
                        <a
                          key={`${comprovanteUrl}-${idx}`}
                          href={comprovanteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#3b82f6', textDecoration: 'underline' }}
                        >
                          {item.nome || `Ver comprovante ${idx + 1}`}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
              {selectedPayment.created_at && (
                <div className="form-group">
                  <label>Data de Registro</label>
                  <input
                    type="text"
                    value={`${new Date(selectedPayment.created_at).toLocaleDateString('pt-BR')} ${new Date(selectedPayment.created_at).toLocaleTimeString('pt-BR')}`}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              {(selectedPayment.justificativa_cancelamento || (selectedBoxForPayment.status_pagamento && selectedBoxForPayment.status_pagamento.toLowerCase() === 'cancelado')) && (
                <div className="form-group">
                  <label>Justificativa do Cancelamento</label>
                  <textarea
                    value={selectedPayment.justificativa_cancelamento || 'N/A'}
                    disabled
                    rows={4}
                    style={{
                      background: '#f3f4f6',
                      cursor: 'not-allowed',
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
              )}
              <div className="modal-actions">
                {selectedBoxForPayment.status_pagamento && selectedBoxForPayment.status_pagamento.toLowerCase() !== 'cancelado' && (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => {
                      setShowCancelPaymentModal(true);
                    }}
                    style={{ marginRight: 'auto' }}
                  >
                    <HiX size={18} />
                    Cancelar Pagamento
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowViewPaymentModal(false);
                    setSelectedPayment(null);
                    setSelectedBoxForPayment(null);
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cancelamento de Pagamento */}
      {showCancelPaymentModal && selectedBoxForPayment && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowCancelPaymentModal(false);
            setCancelJustification('');
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cancelar Pagamento</h2>
              <button
                className="modal-close"
                onClick={() => {
                  setShowCancelPaymentModal(false);
                  setCancelJustification('');
                }}
              >
                <HiX size={24} />
              </button>
            </div>
            <div className="modal-form">
              <div className="form-group">
                <label>Caixa/Mala</label>
                <input
                  type="text"
                  value={`${selectedBoxForPayment.codigo || selectedBoxForPayment.codigo_pacote || ''} - ${selectedBoxForPayment.cliente_nome || selectedBoxForPayment.cliente?.nome || ''}`}
                  disabled
                  style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                />
              </div>
              <div className="form-group">
                <label>Justificativa do Cancelamento *</label>
                <textarea
                  value={cancelJustification}
                  onChange={(e) => setCancelJustification(e.target.value)}
                  placeholder="Informe o motivo do cancelamento (mínimo 10 caracteres)"
                  rows={4}
                  required
                  minLength={10}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                  Mínimo de 10 caracteres. {cancelJustification.length > 0 && (
                    <span style={{ color: cancelJustification.length < 10 ? '#ef4444' : '#10b981' }}>
                      {cancelJustification.length}/10 caracteres
                    </span>
                  )}
                </small>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCancelPaymentModal(false);
                    setCancelJustification('');
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleCancelPayment}
                  disabled={!cancelJustification || cancelJustification.trim().length < 10}
                >
                  <HiX size={18} />
                  Confirmar Cancelamento
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteItemDialog}
        title="Confirmar Exclusão de Item"
        message={`Tem certeza que deseja excluir o item "${itemToDelete?.nome || itemToDelete?.name || 'este item'}"? Esta ação não pode ser desfeita e removerá todas as fotos e vínculos associados.`}
        onConfirm={async () => {
          if (itemToDelete?.id) {
            try {
              await apiService.deleteProduct(itemToDelete.id);
              showSuccess('Item excluído com sucesso!');

              // Atualizar a lista de itens no modal
              if (selectedBoxItems && selectedBoxItems.itens) {
                const updatedItens = selectedBoxItems.itens.filter(item => item.id !== itemToDelete.id);
                setSelectedBoxItems({
                  ...selectedBoxItems,
                  itens: updatedItens
                });
              }

              // Recarregar os dados da caixa para refletir as mudanças
              await loadBoxes();

              setShowDeleteItemDialog(false);
              setItemToDelete(null);
            } catch (error) {
              console.error('Erro ao excluir item:', error);
              showWarning(error?.response?.data?.message || error?.message || 'Erro ao excluir item. Tente novamente.');
            }
          }
        }}
        onCancel={() => {
          setShowDeleteItemDialog(false);
          setItemToDelete(null);
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Modal Ver Destinatário */}
      {showRecipientModal && selectedBoxRecipient && (
        <div className="modal-overlay recipient-print-modal-overlay" onClick={() => {
          setShowRecipientModal(false);
          setRecipientCobrancaId('');
        }}>
          <div
            className="modal-content recipient-print-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header recipient-print-no-print">
              <h3>Dados do Destinatário</h3>
              <button
                type="button"
                className="btn-icon"
                onClick={() => setShowRecipientModal(false)}
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body recipient-print-modal-body">
              <div id="recipient-print-area" className="recipient-print-capture-wrap">
                <div className="recipient-print-sheet">
                  <div className="recipient-print-sheet-inner">
                    <div className="recipient-print-logo-top">
                      <img
                        src="/SENDING_TO.png"
                        alt="SendingTo Express"
                        className="recipient-print-logo"
                      />
                      <span className="recipient-print-status">Retirada segura</span>
                    </div>

                    <div className="recipient-print-heading">
                      <span>Informações de retirada</span>
                      <h4>{selectedBoxRecipient.nome_destinatario || 'Destinatário'}</h4>
                      <p>Apresente o QR Code abaixo no momento da retirada.</p>
                    </div>

                    <div className="recipient-print-code-card">
                      {codigoClienteRecipient ? (
                        <div className="recipient-print-qr-wrap" aria-hidden="true">
                          <QRCodeSVG
                            value={codigoClienteRecipient}
                            size={200}
                            level="M"
                            includeMargin={false}
                            className="recipient-print-qr"
                          />
                        </div>
                      ) : null}
                      <div className="recipient-print-code-info">
                        <strong className="recipient-print-label">Código do cliente</strong>
                        <span className="recipient-print-value recipient-print-value-code">
                          {codigoClienteRecipient || 'Não informado'}
                        </span>
                      </div>
                    </div>

                    {pendenciaRetiradaRecipient && (
                      <div className="recipient-print-payment">
                        <div className="recipient-print-detail-icon recipient-print-detail-icon--pay">
                          <HiCash size={21} />
                        </div>
                        <div className="recipient-print-payment-body">
                          <strong className="recipient-print-local-title">
                            Pagar na retirada
                          </strong>
                          <p className="recipient-print-payment-amount">
                            {formatMoneyWithFlag(
                              pendenciaRetiradaRecipient.valor_local,
                              pendenciaRetiradaRecipient.moeda_local
                            )}
                          </p>
                          <p className="recipient-print-payment-meta">
                            Equivale a{' '}
                            {displayCurrency(pendenciaRetiradaRecipient.valor_brl)}
                            {pendenciaRetiradaRecipient.data_cotacao
                              ? ` · cotação ${pendenciaRetiradaRecipient.data_cotacao}`
                              : ''}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="recipient-print-local">
                      <div className="recipient-print-detail-icon">
                        <HiLocationMarker size={21} />
                      </div>
                      <div>
                        <strong className="recipient-print-local-title">Local da retirada</strong>
                        <p className="recipient-print-local-text">
                          {recipientPickupLocation}
                        </p>
                      </div>
                    </div>

                    {recipientPickupContact ? (
                      <a
                        className="recipient-print-local recipient-print-contact"
                        href={`tel:${recipientPickupContact.tel}`}
                      >
                        <div className="recipient-print-detail-icon">
                          <HiPhone size={20} />
                        </div>
                        <div>
                          <strong className="recipient-print-local-title">Contato no local</strong>
                          <p className="recipient-print-local-text">
                            <strong>{recipientPickupContact.name}</strong>
                            <span>{recipientPickupContact.phone}</span>
                          </p>
                        </div>
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer recipient-print-no-print recipient-print-footer">
              <button
                type="button"
                className="recipient-close-btn"
                onClick={() => setShowRecipientModal(false)}
              >
                Fechar
              </button>
              <button
                type="button"
                className="btn btn-primary recipient-send-btn"
                onClick={handleSendRecipientPickupInfo}
              >
                <HiPaperAirplane size={18} />
                Enviar pelo WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Dados da Entrega */}
      {showDeliveryModal && selectedBoxDelivery && (
        <div className="modal-overlay" onClick={() => setShowDeliveryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
            <div className="modal-header">
              <h3>Dados da Entrega - {selectedBoxDelivery.codigo_pacote || selectedBoxDelivery.codigo || selectedBoxDelivery.id}</h3>
              <button
                className="btn-icon"
                onClick={() => setShowDeliveryModal(false)}
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              {selectedBoxDelivery.entregas && Array.isArray(selectedBoxDelivery.entregas) && selectedBoxDelivery.entregas.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {selectedBoxDelivery.entregas.map((entrega, idx) => (
                    <div
                      key={idx}
                      style={{
                        backgroundColor: '#f9fafb',
                        padding: '1.5rem',
                        borderRadius: '12px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                    >
                      <h4 style={{ marginBottom: '1rem', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
                        Entrega #{idx + 1}
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                        {entrega.codigo_cliente && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Código do Cliente:</strong>
                            <span style={{ color: '#1f2937', fontWeight: '500' }}>{entrega.codigo_cliente}</span>
                          </div>
                        )}
                        {entrega.numero_documento && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Número do Documento:</strong>
                            <span style={{ color: '#1f2937', fontWeight: '500' }}>{entrega.numero_documento}</span>
                          </div>
                        )}
                        {entrega.codigo_acesso && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Código de Acesso:</strong>
                            <span style={{ color: '#1f2937', fontWeight: '500' }}>{entrega.codigo_acesso}</span>
                          </div>
                        )}
                        {entrega.created_at && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.25rem' }}>Data/Hora:</strong>
                            <span style={{ color: '#1f2937', fontWeight: '500' }}>
                              {new Date(entrega.created_at).toLocaleString('pt-BR')}
                            </span>
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                        {entrega.foto_saida_url && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>Foto da Saída:</strong>
                            <img
                              src={normalizeImageUrl(entrega.foto_saida_url)}
                              alt="Foto da Saída"
                              style={{
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer'
                              }}
                              onClick={() => window.open(normalizeImageUrl(entrega.foto_saida_url), '_blank')}
                            />
                          </div>
                        )}
                        {entrega.foto_assinatura_url && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>Foto da Assinatura:</strong>
                            <img
                              src={normalizeImageUrl(entrega.foto_assinatura_url)}
                              alt="Foto da Assinatura"
                              style={{
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer'
                              }}
                              onClick={() => window.open(normalizeImageUrl(entrega.foto_assinatura_url), '_blank')}
                            />
                          </div>
                        )}
                        {entrega.foto_documento_url && (
                          <div>
                            <strong style={{ color: '#6b7280', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem' }}>Foto do Documento:</strong>
                            <img
                              src={normalizeImageUrl(entrega.foto_documento_url)}
                              alt="Foto do Documento"
                              style={{
                                width: '100%',
                                maxHeight: '200px',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer'
                              }}
                              onClick={() => window.open(normalizeImageUrl(entrega.foto_documento_url), '_blank')}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>Nenhuma entrega registrada para este pacote.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Câmera */}
      {showCameraModal && (
        <div className="modal-overlay" onClick={handleCloseCamera}>
          <div className="modal-content camera-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh', width: '90%' }}>
            <div className="modal-header">
              <h3>Tirar Foto do Item</h3>
              <button
                className="btn-icon"
                onClick={handleCloseCamera}
              >
                <HiX size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1rem' }}>
              <div className="camera-container" style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', backgroundColor: '#000', borderRadius: '8px', overflow: 'hidden', marginBottom: '1rem' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="camera-video"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain'
                  }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </div>
              <div className="camera-actions" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', padding: '0.5rem 0' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseCamera}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6b7280',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '120px',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#4b5563';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#6b7280';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <HiX size={18} />
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={toggleCamera}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#17A2B8',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '120px',
                    justifyContent: 'center'
                  }}
                  title={cameraFacingMode === 'environment' ? 'Usar câmera frontal' : 'Usar câmera traseira'}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#138496';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#17A2B8';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <HiRefresh size={18} />
                  {cameraFacingMode === 'environment' ? 'Frontal' : 'Traseira'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={capturePhoto}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    minWidth: '140px',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#059669';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#10b981';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  <HiCamera size={18} />
                  Capturar Foto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="notifications-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Boxes;
